import React, { useMemo, useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudioPlayer, PODCAST_PLAYBACK_RATES } from '../../contexts/AudioPlayerContext';
import { AnimatePresence, motion, type Variants, useScroll, useTransform } from 'framer-motion';
import Globe from 'react-globe.gl';
import SiteBrandedLoader from '../SiteBrandedLoader';
import {
  ArrowLeft,
  ArrowRight,
  Clock3,
  Pause,
  Play,
  Quote,
  SkipBack,
  SkipForward,
  Star,
  UserRound,
  X,
} from 'lucide-react';
import {
  Slider,
  Course,
  Article,
  Podcast,
  VideoPodcast,
  Workshop,
} from '../../types';
import { getImageUrl, getImageUrlWithFallback } from '../../utils/imageUtils';
import { getCoursePathSegment } from '../../utils/coursePath';
import {
  mapCourseToHomeCarouselCard,
  buildHomeCarouselCategoryTabs,
  normalizeCarouselActiveTab,
} from '../../utils/homeCourseCarousel';
import HomeCourseCarouselSection from './HomeCourseCarouselSection';
import { articlesService } from '../../services/api';
import { MOCK_PODCAST_COLUMNS } from '../../data/navMegaMock';
import {
  buildCourseDiscoveryPreviewGroups,
  type ContentCategoryTreeNode,
} from '../../utils/courseDiscoveryHome';
import VideoPodcastModal from '../VideoPodcastModal';
import VideoPodcastCard from '../VideoPodcastCard';
import { FreeBadge } from '../FreeBadge';

export {};

const ARTICLES_PAGE_SIZE = 10;

/** بعد از عوض کردن فایل‌های `public/assets/sliders/*.png` این عدد را یکی زیاد کن تا کش مرورگر نماند. */
const HERO_SLIDER_ASSETS_REV = 12;

type HomeV2Props = {
  sliders: Slider[];
  courses: Course[];
  /** کاروسل «جدیدترین‌ها» از پنل ادمین */
  homepageCarouselNewest?: Course[];
  /** کاروسل «پرطرفدارترین‌ها» از پنل ادمین */
  homepageCarouselPopular?: Course[];
  /** کاروسل «پرفروش‌ترین‌ها» */
  homepageCarouselBestselling?: Course[];
  /** کاروسل «دوره‌های رایگان» — فقط قیمت نهایی صفر */
  homepageCarouselFree?: Course[];
  /** تعداد دسته‌های فعال (ریشه و زیر‌دسته) برای بج کارت «دوره‌ها» */
  courseDiscoveryCategoryCount: number;
  /** درخت دسته‌های دوره برای زیرصفحهٔ «دسته‌بندی دوره‌ها» */
  courseCategoryTree: ContentCategoryTreeNode[];
  articles: Article[];
  podcasts: Podcast[];
  videoPodcasts?: VideoPodcast[];
  workshops: Workshop[];
  onBackToClassic: () => void;
  onOpenPreRegister: (workshop: Workshop | null) => void;
};

type DiscoveryCategoryKey = 'courses' | 'articles' | 'podcasts' | 'videoPodcasts' | 'workshops';
type DiscoveryCategory = {
  key: DiscoveryCategoryKey;
  title: string;
  subtitle: string;
  count: number;
  href: string;
  /** برچسب بعد از عدد بج (پیش‌فرض: «مورد») */
  countLabel?: string;
};

type DiscoveryPreviewGroup = {
  id: string;
  title: string;
  subtitle: string;
  items: Array<{
    id: string;
    label: string;
    href: string;
    badge?: string;
    thumbnailUrl?: string | null;
    description?: string | null;
    accentColor?: string | null;
  }>;
};

const DISCOVERY_CATEGORY_IMAGE_MAP: Record<DiscoveryCategoryKey, string> = {
  courses: '/assets/category-tabs/courses.png?v=2',
  articles: '/assets/category-tabs/articles.png?v=2',
  podcasts: '/assets/category-tabs/podcasts.png?v=2',
  videoPodcasts: '/assets/category-tabs/video-podcasts.png?v=2',
  workshops: '/assets/category-tabs/workshops.png?v=2',
};

const DISCOVERY_CATEGORY_LABEL_MAP: Record<DiscoveryCategoryKey, string> = {
  courses: 'دوره‌ها',
  articles: 'مقالات',
  podcasts: 'پادکست‌ها',
  videoPodcasts: 'ویدیوپادکست‌ها',
  workshops: 'کارگاه‌ها',
};

/** شمارندهٔ ثابت سایر کارت‌ها تا زمان آماده‌شدن ماژول‌ها (فقط «دوره‌ها» از API است) */
const STATIC_HOME_DISCOVERY_COUNTS: Record<Exclude<DiscoveryCategoryKey, 'courses'>, number> = {
  articles: 3,
  podcasts: 2,
  videoPodcasts: 0,
  workshops: 2,
};

type HeroSlide = {
  id: string;
  type: 'image' | 'video';
  source: string;
  /** نسخهٔ باریک موبایل؛ کنار هر `N.png` فایل `Nmobile.png` در public/assets/sliders */
  sourceMobile?: string;
  poster?: string;
  title: string;
  subtitle?: string;
  description?: string;
  altText: string;
  ctaLabel?: string;
  ctaLink?: string;
  openInNewTab?: boolean;
  sortOrder: number;
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8 },
  },
} satisfies Variants;

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
} satisfies Variants;

const curatedAssets = {
  heroImage: '/assets/homeV2/Qodrat Namahdood5.jpg',
  missionImage: '/assets/homeV2/Emotional Podcast2.jpg',
  gallery: [
    '/assets/homeV2/Qodrat Namahdood5.jpg',
    '/assets/homeV2/Pedar Nakhodagah6.jpg',
    '/assets/homeV2/Cast box Cover6.jpg',
    '/assets/homeV2/Emotional Podcast2.jpg',
    '/assets/homeV2/Artboard 2 copy 4.jpg',
    '/assets/homeV2/01.jpg',
    '/assets/homeV2/DSC_0514 (1).jpg',
    '/assets/homeV2/Drifter.jpg',
  ],
  videoPoster: '/assets/homeV2/Sexual Energy Transmutation.jpg',
  mentorImages: [
    '/assets/homeV2/Qodrat Namahdood5.jpg',
    '/assets/homeV2/Pedar Nakhodagah6.jpg',
    '/assets/homeV2/DSC_0514 (1).jpg',
    '/assets/homeV2/Emotional Podcast2.jpg',
  ],
  podcastCovers: [
    '/assets/homeV2/Cast box Cover6.jpg',
    '/assets/homeV2/Soundcloud Header1.jpg',
    '/assets/homeV2/Soundcloud Header2.jpg',
    '/assets/homeV2/Cover CD Getting The Hang of wound Healing3.jpg',
    '/assets/homeV2/Energy-Pool-Moarefi.jpg',
    '/assets/homeV2/Ehsase-Arzeshmandi-Moarefi.jpg',
  ],
};

const DiscoveryCategoryIcon: React.FC<{ categoryKey: DiscoveryCategoryKey }> = ({ categoryKey }) => {
  return (
    <img
      src={DISCOVERY_CATEGORY_IMAGE_MAP[categoryKey]}
      alt={DISCOVERY_CATEGORY_LABEL_MAP[categoryKey]}
      className="h-[6.2rem] w-[6.2rem] object-contain opacity-95 transition-all duration-700 ease-out group-hover:scale-[1.16] group-hover:rotate-3 group-hover:opacity-100 group-hover:drop-shadow-[0_18px_30px_rgba(28,59,155,0.38)]"
      loading="lazy"
    />
  );
};

const DiscoveryCategoryCard: React.FC<{
  category: DiscoveryCategory;
  onOpen: (key: DiscoveryCategoryKey) => void;
  compact?: boolean;
}> = ({ category, onOpen, compact = false }) => {
  return (
    <button
      type="button"
      onClick={() => onOpen(category.key)}
      className={`group w-full min-w-0 text-right transition-all duration-500 ${
        compact ? 'px-4 py-5' : 'px-3 py-5 sm:px-4'
      }`}
      aria-label={`انتخاب دسته ${category.title}`}
    >
      <div className="relative flex flex-col items-center gap-4 overflow-hidden rounded-[28px] border border-white/70 bg-white/65 px-4 py-6 backdrop-blur-[1px] transition-all duration-300 group-hover:-translate-y-1 group-hover:bg-white group-hover:shadow-[0_12px_32px_-16px_rgba(28,59,155,0.25)] sm:px-5 sm:py-7">
        <div className="inline-flex h-[6rem] w-[6rem] items-center justify-center">
          <DiscoveryCategoryIcon categoryKey={category.key} />
        </div>
        <p className="text-sm font-bold text-[#0f172a] transition-colors duration-300 group-hover:text-[#1C3B9B] sm:text-base">{category.title}</p>
      </div>
    </button>
  );
};

const HEX_COLOR = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

const CourseDiscoveryThumb: React.FC<{
  src: string | null | undefined;
  label: string;
  accent: string | null | undefined;
}> = ({ src, label, accent }) => {
  const tint = accent && HEX_COLOR.test(accent.trim()) ? accent.trim() : null;
  const initial = label.trim().slice(0, 1) || '—';

  return (
    <div className="relative h-[4.75rem] w-[5.6rem] shrink-0 overflow-hidden rounded-2xl bg-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] ring-2 ring-white/95">
      {src ? (
        <>
          <img
            src={src}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 ease-out group-hover/thumb:scale-105 group-hover/thumb:rotate-[0.5deg]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/35 via-transparent to-transparent opacity-60" />
        </>
      ) : (
        <div
          className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#1C3B9B]/23 via-[#7c3aed]/16 to-[#C11277]/23"
          style={
            tint
              ? {
                  background: `linear-gradient(145deg, ${tint}59, rgba(124,58,237,0.14), rgba(28,59,155,0.12))`,
                }
              : undefined
          }
        >
          <span className="text-xl font-black text-white drop-shadow-md" aria-hidden>
            {initial}
          </span>
        </div>
      )}
    </div>
  );
};

/** زیرصفحهٔ دستهٔ دوره — کارت‌ها با تصویر و پیش‌زمینهٔ شیشه‌ای */
const CourseDiscoveryCoursesPanel: React.FC<{
  groups: DiscoveryPreviewGroup[];
}> = ({ groups }) => {
  const navigate = useNavigate();

  const totalBuckets = groups.length;
  const totalRows = groups.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-slate-200/60 bg-gradient-to-b from-white via-[#f8fafc] to-[#eef2ff]/90 p-[1px] shadow-[0_28px_64px_-36px_rgba(28,59,155,0.45)] sm:rounded-[32px]">
      <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#7c3aed]/28 to-transparent" />
      <div className="relative rounded-[27px] bg-white/55 px-4 py-5 backdrop-blur-[10px] sm:rounded-[31px] sm:px-6 sm:py-7">
        <div className="mb-5 flex flex-col gap-3 border-b border-slate-200/60 pb-4 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:pb-5">
          <div className="text-right">
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-[#7c3aed]/90">کتابخانه</p>
            <h4 className="mt-1 text-lg font-black text-slate-900 sm:text-xl">دسته‌بندی دوره‌ها</h4>
            <p className="mt-1 text-xs font-medium text-slate-500">
              {totalBuckets.toLocaleString('fa-IR')} محور · {totalRows.toLocaleString('fa-IR')} مسیر
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/courses')}
            className="inline-flex items-center justify-center gap-2 self-stretch rounded-2xl bg-gradient-to-l from-[#1C3B9B] to-[#7c3aed] px-4 py-2.5 text-sm font-bold text-white shadow-[0_14px_32px_-18px_rgba(28,59,155,0.75)] transition hover:brightness-105 active:scale-[0.99] sm:self-auto"
          >
            <span>کتابخانهٔ کامل</span>
            <ArrowLeft className="h-4 w-4 opacity-90" aria-hidden />
          </button>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {groups.map((group) => (
            <div
              key={group.id}
              className="flex flex-col rounded-[26px] border border-slate-200/70 bg-white/90 p-4 shadow-[0_16px_44px_-30px_rgba(15,23,42,0.35)] ring-1 ring-white/80 transition duration-300 hover:border-[#1C3B9B]/22 hover:shadow-[0_22px_50px_-28px_rgba(28,59,155,0.32)] sm:p-5"
            >
              <div className="mb-3 flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
                <div className="min-w-0 text-right">
                  <p className="text-base font-black text-slate-900">{group.title}</p>
                  <p className="mt-1 text-[11px] font-semibold leading-relaxed text-slate-500">{group.subtitle}</p>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100/90 px-2.5 py-1 text-[10px] font-black text-slate-600 tabular-nums ring-1 ring-slate-200/80">
                  {group.items.length.toLocaleString('fa-IR')}
                </span>
              </div>
              <div className="flex flex-col gap-2.5">
                {group.items.length > 0 ? (
                  group.items.map((item) => (
                    <button
                      key={`${group.id}-${item.id}`}
                      type="button"
                      onClick={() => navigate(item.href)}
                      className="group/thumb flex w-full items-center gap-3 rounded-2xl border border-slate-200/75 bg-gradient-to-l from-white to-slate-50/90 p-2.5 text-right shadow-[0_6px_20px_-14px_rgba(15,23,42,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#1C3B9B]/28 hover:from-[#fafbff] hover:to-[#f1f5ff]/95 hover:shadow-[0_18px_40px_-22px_rgba(28,59,155,0.38)]"
                    >
                      <CourseDiscoveryThumb
                        src={item.thumbnailUrl}
                        label={item.label}
                        accent={item.accentColor}
                      />
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-sm font-bold leading-snug text-slate-900 transition group-hover/thumb:text-[#1C3B9B]">
                          {item.label}
                        </p>
                        {item.description ? (
                          <p className="line-clamp-2 text-[11px] leading-relaxed text-slate-500">{item.description}</p>
                        ) : (
                          <p className="text-[11px] text-slate-400">مشاهده دوره‌های این بخش</p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5 self-stretch justify-center ps-1">
                        {item.badge ? (
                          <span className="rounded-full bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                            {item.badge}
                          </span>
                        ) : null}
                        <ArrowLeft
                          className="h-4 w-4 text-slate-300 transition group-hover/thumb:-translate-x-0.5 group-hover/thumb:text-[#1C3B9B]"
                          aria-hidden
                        />
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="py-6 text-center text-xs text-slate-500">موردی موجود نیست</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const HomeV2: React.FC<HomeV2Props> = ({
  sliders,
  courses,
  homepageCarouselNewest = [],
  homepageCarouselPopular = [],
  homepageCarouselBestselling = [],
  homepageCarouselFree = [],
  courseDiscoveryCategoryCount,
  courseCategoryTree,
  articles,
  podcasts,
  videoPodcasts = [],
  workshops,
  onBackToClassic,
  onOpenPreRegister,
}) => {
  const navigate = useNavigate();
  const [discoveryStep, setDiscoveryStep] = useState<'topics' | 'subcategories'>('topics');
  const [activeDiscoveryCategory, setActiveDiscoveryCategory] =
    useState<DiscoveryCategoryKey>('courses');

  const stats = [
    { label: 'سال فعالیت', value: '8+' },
    { label: 'کارگاه برگزار شده', value: '2000+' },
    { label: 'شرکت‌کننده', value: '200K+' },
    { label: 'دوره و برنامه', value: `${Math.max(courses.length, 6)}+` },
  ];

  const transformationLines = [
    'من حقیقی، مسیر ساختن یک زندگی آگاهانه است—با معنا، رشد و عمل.',
    'ما در کنار شما هستیم تا خودشناسی را از «دانستن» به «تبدیل شدن» تبدیل کنید.',
    'تمرین‌ها و تکنیک‌های راهبردی، برای عبور از موانع روانی، جسمی، مالی و عاطفی طراحی شده‌اند.',
    'بهترین نتایج وقتی اتفاق می‌افتد که تصمیم بگیرید و پیوسته اقدام کنید.',
    'این مسیر هم حضوری است و هم آنلاین—هرجا هستید می‌توانید هم‌مسیر شوید.',
    'شما نیز بخشی از داستان ما هستید.',
  ];

  const pillars = [
    {
      label: 'خودشناسی',
      title: 'خودشناسی و خودآگاهی',
      description:
        'شناخت عمیق خود، روشن‌کردن مسیر تصمیم‌ها و ساختن پایه‌های یک تغییر پایدار.',
    },
    {
      label: 'ناخودآگاه',
      title: 'کار با ناخودآگاه',
      description:
        'بازنویسی الگوها، رهاسازی گره‌ها و حرکت از تکرار به سمت انتخاب آگاهانه.',
    },
    {
      label: 'روابط',
      title: 'روابط عاطفی و مالی',
      description:
        'مهارت‌های ارتباطی و نگاه تازه به پول و ارزشمندی؛ برای ساختن روابط سالم و پایدار.',
    },
  ];

  const missionHighlights = [
    { value: '8+', label: 'سال فعالیت' },
    { value: '2000+', label: 'کارگاه آموزشی' },
    { value: '200K+', label: 'شرکت‌کننده' },
  ];

  const galleryImages = useMemo(() => {
    const sources = sliders
      .map((slide) => getImageUrl(slide.image))
      .filter((image): image is string => Boolean(image));

    return Array.from(new Set([...sources, ...curatedAssets.gallery])).slice(0, 9);
  }, [sliders]);

  const missionImage = curatedAssets.missionImage ?? galleryImages[0];

  const testimonialEntries = useMemo(() => {
    if (articles.length > 0) {
      return articles.slice(0, 3).map((article, index) => ({
        quote:
          article.excerpt ??
          'این مسیر ذهنم را بازنویسی کرد و جسارت تصمیم‌گیری مرا چند برابر نمود.',
        name: article.title ?? `شرکت‌کننده ${index + 1}`,
        role: 'عضو مجموعه من حقیقی',
      }));
    }

    return [
      {
        quote:
          'هر لحظه این تجربه مثل آتش بود؛ محدودیت‌هایم سوخت و چشم‌انداز تازه‌ای پیدا کردم.',
        name: 'مریم — رهبر فروش',
        role: 'شرکت‌کننده کارگاه',
      },
      {
        quote:
          'من یاد گرفتم رشد را انتخاب کنم و برایش سیستم بسازم. این شروع دوباره من است.',
        name: 'کاوه — کارآفرین تکنولوژی',
        role: 'شرکت کننده کارگاه',
      },
      {
        quote:
          'قدرت واقعی را در تنظیم انرژی روزانه و تصمیم‌های قاطع پیدا کردم.',
        name: 'هلیا — مربی سلامت',
        role: 'دانشجوی برنامه تحول',
      },
    ];
  }, [articles]);

  const primaryWorkshop = workshops[0] ?? null;

  // Articles section: 10 per page with pagination (fetched in this component)
  const [articlesList, setArticlesList] = useState<Article[]>([]);
  const [articlesPage, setArticlesPage] = useState(1);
  const [articlesTotalPages, setArticlesTotalPages] = useState(1);
  const [articlesTotal, setArticlesTotal] = useState(0);
  const [articlesLoading, setArticlesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setArticlesLoading(true);
    articlesService.getPublished({ page: articlesPage, limit: ARTICLES_PAGE_SIZE })
      .then((res) => {
        if (!cancelled) {
          setArticlesList(res.data ?? []);
          setArticlesTotalPages(res.meta?.totalPages ?? 1);
          setArticlesTotal(res.meta?.total ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) setArticlesList([]);
      })
      .finally(() => {
        if (!cancelled) setArticlesLoading(false);
      });
    return () => { cancelled = true; };
  }, [articlesPage]);

  const featuredPodcasts = useMemo(() => {
    return podcasts;
  }, [podcasts]);

  const newestCourseCards = useMemo(
    () => homepageCarouselNewest.map(mapCourseToHomeCarouselCard),
    [homepageCarouselNewest],
  );
  const popularCourseCards = useMemo(
    () => homepageCarouselPopular.map(mapCourseToHomeCarouselCard),
    [homepageCarouselPopular],
  );
  const bestSellingCourseCards = useMemo(
    () => homepageCarouselBestselling.map(mapCourseToHomeCarouselCard),
    [homepageCarouselBestselling],
  );
  const freeCourseCards = useMemo(
    () => homepageCarouselFree.map(mapCourseToHomeCarouselCard),
    [homepageCarouselFree],
  );

  const newestCategoryTabs = useMemo(
    () => buildHomeCarouselCategoryTabs(newestCourseCards),
    [newestCourseCards],
  );
  const popularCategoryTabs = useMemo(
    () => buildHomeCarouselCategoryTabs(popularCourseCards),
    [popularCourseCards],
  );
  const bestSellingCategoryTabs = useMemo(
    () => buildHomeCarouselCategoryTabs(bestSellingCourseCards),
    [bestSellingCourseCards],
  );
  const freeCategoryTabs = useMemo(
    () => buildHomeCarouselCategoryTabs(freeCourseCards),
    [freeCourseCards],
  );

  const [newestActiveTab, setNewestActiveTab] = useState('all');
  const [popularActiveTab, setPopularActiveTab] = useState('all');
  const [bestSellingActiveTab, setBestSellingActiveTab] = useState('all');
  const [freeActiveTab, setFreeActiveTab] = useState('all');

  useEffect(() => {
    setNewestActiveTab((t) => normalizeCarouselActiveTab(t, newestCategoryTabs));
  }, [newestCategoryTabs]);

  useEffect(() => {
    setPopularActiveTab((t) => normalizeCarouselActiveTab(t, popularCategoryTabs));
  }, [popularCategoryTabs]);

  useEffect(() => {
    setBestSellingActiveTab((t) => normalizeCarouselActiveTab(t, bestSellingCategoryTabs));
  }, [bestSellingCategoryTabs]);

  useEffect(() => {
    setFreeActiveTab((t) => normalizeCarouselActiveTab(t, freeCategoryTabs));
  }, [freeCategoryTabs]);

  const filteredNewestCourseCards = useMemo(() => {
    if (newestActiveTab === 'all') {
      return newestCourseCards;
    }
    return newestCourseCards.filter((course) => course.categoryFilterKey === newestActiveTab);
  }, [newestActiveTab, newestCourseCards]);

  const filteredPopularCourseCards = useMemo(() => {
    if (popularActiveTab === 'all') {
      return popularCourseCards;
    }
    return popularCourseCards.filter((course) => course.categoryFilterKey === popularActiveTab);
  }, [popularActiveTab, popularCourseCards]);

  const newestCarouselRef = useRef<HTMLDivElement | null>(null);
  const [isNewestCarouselHovered, setIsNewestCarouselHovered] = useState(false);

  const handleNewestCarouselScroll = useCallback((direction: 'next' | 'prev') => {
    if (!newestCarouselRef.current) {
      return;
    }

    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const step = 320;
    const delta = direction === 'next'
      ? (isRtl ? -step : step)
      : (isRtl ? step : -step);

    newestCarouselRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const popularCarouselRef = useRef<HTMLDivElement | null>(null);
  const [isPopularCarouselHovered, setIsPopularCarouselHovered] = useState(false);

  const handlePopularCarouselScroll = useCallback((direction: 'next' | 'prev') => {
    if (!popularCarouselRef.current) {
      return;
    }

    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const step = 320;
    const delta = direction === 'next'
      ? (isRtl ? -step : step)
      : (isRtl ? step : -step);

    popularCarouselRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const filteredBestSellingCards = useMemo(() => {
    if (bestSellingActiveTab === 'all') {
      return bestSellingCourseCards;
    }
    return bestSellingCourseCards.filter(
      (course) => course.categoryFilterKey === bestSellingActiveTab,
    );
  }, [bestSellingActiveTab, bestSellingCourseCards]);

  const filteredFreeCards = useMemo(() => {
    if (freeActiveTab === 'all') {
      return freeCourseCards;
    }
    return freeCourseCards.filter((course) => course.categoryFilterKey === freeActiveTab);
  }, [freeActiveTab, freeCourseCards]);
  const createLoopedCarouselItems = useCallback(<T extends { id: string }>(items: T[], minimumCount = 10): T[] => {
    if (items.length === 0) {
      return [];
    }
    if (items.length >= minimumCount) {
      return items;
    }

    const repeats = Math.ceil(minimumCount / items.length);
    return Array.from({ length: repeats }).flatMap((_, repeatIndex) =>
      items.map((item, itemIndex) => ({
        ...item,
        id: `${item.id}-loop-${repeatIndex + 1}-${itemIndex + 1}`,
      }))
    );
  }, []);
  const loopedNewestCourseCards = useMemo(
    () => createLoopedCarouselItems(filteredNewestCourseCards, 12),
    [createLoopedCarouselItems, filteredNewestCourseCards]
  );
  const loopedPopularCourseCards = useMemo(
    () => createLoopedCarouselItems(filteredPopularCourseCards, 12),
    [createLoopedCarouselItems, filteredPopularCourseCards]
  );
  const loopedBestSellingCards = useMemo(
    () => createLoopedCarouselItems(filteredBestSellingCards, 12),
    [createLoopedCarouselItems, filteredBestSellingCards]
  );
  const loopedFreeCards = useMemo(
    () => createLoopedCarouselItems(filteredFreeCards, 12),
    [createLoopedCarouselItems, filteredFreeCards]
  );

  useEffect(() => {
    const pauseByHover = isNewestCarouselHovered && typeof window !== 'undefined' && window.innerWidth < 1024;
    if (pauseByHover || !newestCarouselRef.current || loopedNewestCourseCards.length < 2) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!newestCarouselRef.current) {
        return;
      }

      const el = newestCarouselRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (Math.abs(el.scrollLeft) >= maxScroll - 8) {
        el.scrollLeft = 0;
        return;
      }
      handleNewestCarouselScroll('next');
    }, 3800);

    return () => window.clearInterval(intervalId);
  }, [handleNewestCarouselScroll, isNewestCarouselHovered, loopedNewestCourseCards.length]);

  useEffect(() => {
    const pauseByHover = isPopularCarouselHovered && typeof window !== 'undefined' && window.innerWidth < 1024;
    if (pauseByHover || !popularCarouselRef.current || loopedPopularCourseCards.length < 2) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!popularCarouselRef.current) {
        return;
      }

      const el = popularCarouselRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (Math.abs(el.scrollLeft) >= maxScroll - 8) {
        el.scrollLeft = 0;
        return;
      }
      handlePopularCarouselScroll('next');
    }, 3800);

    return () => window.clearInterval(intervalId);
  }, [handlePopularCarouselScroll, isPopularCarouselHovered, loopedPopularCourseCards.length]);

  const bestSellingCarouselRef = useRef<HTMLDivElement | null>(null);
  const freeCarouselRef = useRef<HTMLDivElement | null>(null);
  const [isBestSellingHovered, setIsBestSellingHovered] = useState(false);
  const [isFreeCarouselHovered, setIsFreeCarouselHovered] = useState(false);
  const testimonialCarouselRef = useRef<HTMLDivElement | null>(null);
  const testimonialStripMobileRef = useRef<HTMLDivElement | null>(null);
  const testimonialStripMobilePausedRef = useRef(false);
  const [isTestimonialHovered, setIsTestimonialHovered] = useState(false);
  const latestArticlesCarouselRef = useRef<HTMLDivElement | null>(null);
  const [isLatestArticlesHovered, setIsLatestArticlesHovered] = useState(false);

  const podcastCategoryTabs = useMemo(
    () => [
      { key: 'all', label: 'همه' },
      { key: 'mindset', label: 'تحول فردی' },
      { key: 'relationship', label: 'روابط عاطفی' },
      { key: 'finance', label: 'مالی و ثروت' },
      { key: 'subconscious', label: 'ناخودآگاه' },
    ],
    []
  );
  const [podcastActiveTab, setPodcastActiveTab] = useState('all');
  const podcastCards = useMemo(() => {
    const categoryCycle = ['mindset', 'relationship', 'finance', 'subconscious'] as const;
    const baseCards = featuredPodcasts.slice(0, 12).map((podcast, index) => {
      const category = categoryCycle[index % categoryCycle.length];
      const thumbnail = podcast.thumbnail
        ? getImageUrlWithFallback(podcast.thumbnail, curatedAssets.podcastCovers[index % curatedAssets.podcastCovers.length])
        : curatedAssets.podcastCovers[index % curatedAssets.podcastCovers.length];
      return {
        ...podcast,
        category,
        thumbnail,
      };
    });
    if (baseCards.length >= 12) {
      return baseCards;
    }
    const needed = 12 - baseCards.length;
    const clones = Array.from({ length: needed }).map((_, idx) => {
      const source = baseCards[idx % Math.max(baseCards.length, 1)] ?? featuredPodcasts[0];
      const category = categoryCycle[(idx + baseCards.length) % categoryCycle.length];
      if (!source) {
        return null;
      }
      return {
        ...source,
        id: `${source.id}-mix-${idx + 1}`,
        title: `${source.title} | نسخه ویژه`,
        category,
        thumbnail: curatedAssets.podcastCovers[(idx + 2) % curatedAssets.podcastCovers.length],
      };
    }).filter(Boolean);

    return [...baseCards, ...(clones as typeof baseCards)];
  }, [featuredPodcasts]);
  const filteredPodcastCards = useMemo(() => {
    if (podcastActiveTab === 'all') {
      return podcastCards;
    }
    return podcastCards.filter((item) => item.category === podcastActiveTab);
  }, [podcastActiveTab, podcastCards]);
  const loopedPodcastCards = useMemo(
    () => createLoopedCarouselItems(filteredPodcastCards, 12),
    [createLoopedCarouselItems, filteredPodcastCards]
  );
  const podcastCarouselRef = useRef<HTMLDivElement | null>(null);
  const [isPodcastCarouselHovered, setIsPodcastCarouselHovered] = useState(false);

  const handlePodcastCarouselScroll = useCallback((direction: 'next' | 'prev') => {
    if (!podcastCarouselRef.current) {
      return;
    }
    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const step = 320;
    const delta = direction === 'next' ? (isRtl ? -step : step) : (isRtl ? step : -step);
    podcastCarouselRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);


  const handleBestSellingCarouselScroll = useCallback((direction: 'next' | 'prev') => {
    if (!bestSellingCarouselRef.current) {
      return;
    }
    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const step = 320;
    const delta = direction === 'next' ? (isRtl ? -step : step) : (isRtl ? step : -step);
    bestSellingCarouselRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const handleFreeCarouselScroll = useCallback((direction: 'next' | 'prev') => {
    if (!freeCarouselRef.current) {
      return;
    }
    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const step = 320;
    const delta = direction === 'next' ? (isRtl ? -step : step) : (isRtl ? step : -step);
    freeCarouselRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const testimonialCards = useMemo(
    () => [
      {
        id: 'testimonial-2',
        name: 'امیر حسین سلطان محمدی',
        role: 'مدیر بازاریابی',
        avatar: '/assets/fan/امیر حسین سلطان محمدی.JPG',
        rating: 5,
        text: 'محتوا کاملاً کاربردی بود و خیلی سریع تونستم نکاتش رو در کارم اجرا کنم.',
      },
      {
        id: 'testimonial-4',
        name: 'پژمان لنگری',
        role: 'برنامه‌نویس وب',
        avatar: '/assets/fan/پژمان لنگری.jpg',
        rating: 5,
        text: 'همه چیز منظم و حرفه‌ای بود و کیفیت ارائه واقعاً از انتظارم بالاتر بود.',
      },
      {
        id: 'testimonial-5',
        name: 'بهناز فاطمی',
        role: 'طراح گرافیک',
        avatar: '/assets/fan/بهناز فاطمی.jpg',
        rating: 5,
        text: 'خیلی خوشحالم که شرکت کردم؛ مطالب دقیق و قابل استفاده بودند و خروجی عالی گرفتم.',
      },
      {
        id: 'testimonial-6',
        name: 'فاطمه شمس',
        role: 'مدرس زبان',
        avatar: '/assets/fan/فاطمه شمس.jpg',
        rating: 5,
        text: 'از نظر کیفیت آموزش و پشتیبانی واقعاً عالی بود و تجربه خیلی مثبتی برام ساخت.',
      },
      {
        id: 'testimonial-7',
        name: 'محمد انیسی',
        role: 'تحلیل‌گر داده',
        avatar: '/assets/fan/محمد انیسی.png',
        rating: 5,
        text: 'این دوره برای من خیلی مفید بود و باعث شد با دید بهتری مسیرم رو ادامه بدم.',
      },
      {
        id: 'testimonial-8',
        name: 'سید میثاق چایی فروش',
        role: 'مدیر پروژه',
        avatar: '/assets/fan/سید میثاق چایی فروش.JPG',
        rating: 5,
        text: 'با خیال راحت می‌تونم بگم یکی از بهترین تجربه‌های آموزشی من تا الان بوده.',
      },
      {
        id: 'testimonial-9',
        name: 'نرگس مظفریان',
        role: 'حسابدار',
        avatar: '/assets/fan/نرگس مظفریان.jpg',
        rating: 5,
        text: 'هم محتوای دوره عالی بود و هم انرژی مثبت مدرس و تیم واقعاً انگیزه‌بخش بود.',
      },
      {
        id: 'testimonial-10',
        name: 'الهام آبسالان',
        role: 'کارشناس آموزش',
        avatar: '/assets/fan/الهام آبسالان.JPG',
        rating: 5,
        text: 'مطالب کاملاً شفاف و قابل فهم بودند و کمک کردند سریع‌تر به هدفم نزدیک بشم.',
      },
      {
        id: 'testimonial-11',
        name: 'امیر خوانساری',
        role: 'کارآفرین',
        avatar: '/assets/fan/امیر خوانساری.png',
        rating: 5,
        text: 'کیفیت کار واقعاً حرفه‌ایه و من از انتخابم برای شرکت در این دوره کاملاً راضی‌ام.',
      },
      {
        id: 'testimonial-12',
        name: 'محمد رضا ترابیان',
        role: 'مدیر فروش',
        avatar: '/assets/fan/محمد رضا ترابیان.jpg',
        rating: 5,
        text: 'منظم، کاربردی و الهام‌بخش؛ دقیقاً چیزی بود که از یک آموزش حرفه‌ای انتظار داشتم.',
      },
      {
        id: 'testimonial-13',
        name: 'آنسه معروف نژاد',
        role: 'کارشناس دیجیتال مارکتینگ',
        avatar: '/assets/fan/آنسه معروف نژاد.jpg',
        rating: 5,
        text: 'از کیفیت محتوای دوره و نحوه انتقال مطالب واقعاً لذت بردم و نتیجه گرفتم.',
      },
      {
        id: 'testimonial-14',
        name: 'سوگند میرزاپور',
        role: 'روانشناس',
        avatar: '/assets/fan/سوگند میرزاپور.jpg',
        rating: 5,
        text: 'تجربه‌ای بسیار مثبت و ارزشمند بود و من با رضایت کامل این دوره رو گذروندم.',
      },
      {
        id: 'testimonial-15',
        name: 'کتایون پاک بین',
        role: 'مدیر منابع انسانی',
        avatar: '/assets/fan/کتایون پاک بین.jpg',
        rating: 5,
        text: 'همه چیز حرفه‌ای و باکیفیت بود و قطعاً باز هم از دوره‌های بعدی استفاده می‌کنم.',
      },
    ],
    []
  );
  const testimonialAvatarFallback = '/assets/fan/بهناز فاطمی.jpg';
  const mentorPartnerLogos = useMemo(
    () =>
      Array.from({ length: 11 }, (_, idx) => ({
        id: `partner-${idx + 1}`,
        src: `/assets/mentor-organizations/${idx + 1}.png`,
        alt: `سازمان همکار ${idx + 1}`,
      })),
    []
  );
  const celebrityLogos = useMemo(
    () => [
      { src: '/assets/celebrities/Fariba-Kosari.jpg', name: 'فریبا کوثری' },
      { src: '/assets/celebrities/Farkhonde-Farmanizade.jpg', name: 'فرخنده فرمانی‌زاده' },
      { src: '/assets/celebrities/Ladan-Mostofi.jpg', name: 'لادن مستوفی' },
      { src: '/assets/celebrities/Majid-Torkaman.jpg', name: 'مجید ترکمان' },
      { src: '/assets/celebrities/Mohammad-Ali-Bahmani.jpg', name: 'محمدعلی بهمنی' },
      { src: '/assets/celebrities/Naser-Zeynali.jpg', name: 'ناصر زینلی' },
      { src: '/assets/celebrities/Sharare-Rokham.jpg', name: 'شراره رخام' },
      { src: '/assets/celebrities/reza-banafshekhah.jpg', name: 'رضا بنفشه‌خواه' },
    ],
    [],
  );
  const celebrityTickerItems = useMemo(() => [...celebrityLogos, ...celebrityLogos], [celebrityLogos]);
  const mobileCelebrityLoopSlides = useMemo(
    () => [...celebrityLogos, ...celebrityLogos],
    [celebrityLogos],
  );
  const desktopCelebrityStripRef = useRef<HTMLDivElement | null>(null);
  const mobileCelebrityStripRef = useRef<HTMLDivElement | null>(null);
  const mobileCelebrityIndexRef = useRef(0);
  const mobileCelebrityUserInteractingRef = useRef(false);
  const mobileCelebrityResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileCelebrityScrollSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isCelebrityDragging, setIsCelebrityDragging] = useState(false);
  const celebrityDragStartXRef = useRef(0);
  const celebrityDragStartScrollRef = useRef(0);
  const mobileDiscoveryTickerRef = useRef<HTMLDivElement | null>(null);
  const mobileDiscoveryIndexRef = useRef(0);
  const mobileDiscoveryUserInteractingRef = useRef(false);
  const mobileDiscoveryResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mobileDiscoveryScrollSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loopedTestimonialCards = useMemo(
    () => createLoopedCarouselItems(testimonialCards, 10),
    [createLoopedCarouselItems, testimonialCards]
  );
  const latestArticleTabs = useMemo(
    () => [
      { key: 'all', label: 'همه' },
      { key: 'mindset', label: 'تحول فردی' },
      { key: 'relationship', label: 'روابط عاطفی' },
      { key: 'finance', label: 'مالی و ثروت' },
      { key: 'subconscious', label: 'ناخودآگاه' },
      { key: 'success', label: 'موفقیت' },
    ],
    []
  );
  const [latestArticlesActiveTab, setLatestArticlesActiveTab] = useState('all');
  const latestArticlesCards = useMemo(() => {
    const categoryCycle = ['mindset', 'relationship', 'finance', 'subconscious', 'success'] as const;
    const source = articlesList.length > 0 ? articlesList : articles;
    return source.slice(0, 12).map((article, index) => {
      const fallbackImage = curatedAssets.gallery[(index + 1) % curatedAssets.gallery.length];
      const image = article.featuredImage
        ? getImageUrlWithFallback(article.featuredImage, fallbackImage)
        : fallbackImage;
      return {
        ...article,
        image,
        category: categoryCycle[index % categoryCycle.length],
      };
    });
  }, [articles, articlesList]);
  const filteredLatestArticlesCards = useMemo(() => {
    if (latestArticlesActiveTab === 'all') {
      return latestArticlesCards;
    }
    return latestArticlesCards.filter((article) => article.category === latestArticlesActiveTab);
  }, [latestArticlesActiveTab, latestArticlesCards]);
  const loopedLatestArticlesCards = useMemo(
    () => createLoopedCarouselItems(filteredLatestArticlesCards, 12),
    [createLoopedCarouselItems, filteredLatestArticlesCards]
  );

  const handleTestimonialScroll = useCallback((direction: 'next' | 'prev') => {
    if (!testimonialCarouselRef.current) {
      return;
    }
    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const step = 360;
    const delta = direction === 'next' ? (isRtl ? -step : step) : (isRtl ? step : -step);
    testimonialCarouselRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const handleLatestArticlesScroll = useCallback((direction: 'next' | 'prev') => {
    if (!latestArticlesCarouselRef.current) {
      return;
    }
    const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const step = 340;
    const delta = direction === 'next' ? (isRtl ? -step : step) : (isRtl ? step : -step);
    latestArticlesCarouselRef.current.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const pauseByHover = isBestSellingHovered && typeof window !== 'undefined' && window.innerWidth < 1024;
    if (pauseByHover || !bestSellingCarouselRef.current || loopedBestSellingCards.length < 2) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (!bestSellingCarouselRef.current) {
        return;
      }
      const el = bestSellingCarouselRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (Math.abs(el.scrollLeft) >= maxScroll - 8) {
        el.scrollLeft = 0;
        return;
      }
      handleBestSellingCarouselScroll('next');
    }, 3800);
    return () => window.clearInterval(intervalId);
  }, [handleBestSellingCarouselScroll, isBestSellingHovered, loopedBestSellingCards.length]);

  useEffect(() => {
    const pauseByHover = isFreeCarouselHovered && typeof window !== 'undefined' && window.innerWidth < 1024;
    if (pauseByHover || !freeCarouselRef.current || loopedFreeCards.length < 2) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (!freeCarouselRef.current) {
        return;
      }
      const el = freeCarouselRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (Math.abs(el.scrollLeft) >= maxScroll - 8) {
        el.scrollLeft = 0;
        return;
      }
      handleFreeCarouselScroll('next');
    }, 3800);
    return () => window.clearInterval(intervalId);
  }, [handleFreeCarouselScroll, isFreeCarouselHovered, loopedFreeCards.length]);

  useEffect(() => {
    const pauseByHover = isPodcastCarouselHovered && typeof window !== 'undefined' && window.innerWidth < 1024;
    if (pauseByHover || !podcastCarouselRef.current || loopedPodcastCards.length < 2) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (!podcastCarouselRef.current) {
        return;
      }
      const el = podcastCarouselRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (Math.abs(el.scrollLeft) >= maxScroll - 8) {
        el.scrollLeft = 0;
        return;
      }
      handlePodcastCarouselScroll('next');
    }, 3800);
    return () => window.clearInterval(intervalId);
  }, [handlePodcastCarouselScroll, isPodcastCarouselHovered, loopedPodcastCards.length]);

  useEffect(() => {
    const pauseByHover = isTestimonialHovered && typeof window !== 'undefined' && window.innerWidth < 1024;
    if (pauseByHover || !testimonialCarouselRef.current || loopedTestimonialCards.length < 2) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (!testimonialCarouselRef.current) {
        return;
      }
      const el = testimonialCarouselRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (Math.abs(el.scrollLeft) >= maxScroll - 8) {
        el.scrollLeft = 0;
        return;
      }
      handleTestimonialScroll('next');
    }, 4500);
    return () => window.clearInterval(intervalId);
  }, [handleTestimonialScroll, isTestimonialHovered, loopedTestimonialCards.length]);

  /** موبایل: شروع از انتهای نوار؛ اسکرول خودکار به جلو با مکث؛ رسیدن به انتها پرش به ابتدا. */
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth >= 640) {
      return;
    }
    const el = testimonialStripMobileRef.current;
    if (!el || testimonialCards.length < 2) {
      return;
    }
    const alignToEnd = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll > 0) {
        el.scrollLeft = maxScroll;
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(alignToEnd);
    });
  }, [testimonialCards.length]);

  useEffect(() => {
    if (testimonialCards.length < 2) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (typeof window === 'undefined' || window.innerWidth >= 640) {
        return;
      }
      if (testimonialStripMobilePausedRef.current) {
        return;
      }
      const el = testimonialStripMobileRef.current;
      if (!el) {
        return;
      }
      const card = el.querySelector('[data-testimonial-strip-card]') as HTMLElement | null;
      const gap = 12;
      const step = card ? card.getBoundingClientRect().width + gap : 220;
      const maxScroll = el.scrollWidth - el.clientWidth - 4;
      if (el.scrollLeft >= maxScroll - 6) {
        el.scrollLeft = 0;
      }
      el.scrollBy({ left: step, behavior: 'smooth' });
    }, 4600);
    return () => window.clearInterval(intervalId);
  }, [testimonialCards.length]);

  useEffect(() => {
    const pauseByHover = isLatestArticlesHovered && typeof window !== 'undefined' && window.innerWidth < 1024;
    if (pauseByHover || !latestArticlesCarouselRef.current || loopedLatestArticlesCards.length < 2) {
      return;
    }
    const intervalId = window.setInterval(() => {
      if (!latestArticlesCarouselRef.current) {
        return;
      }
      const el = latestArticlesCarouselRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (Math.abs(el.scrollLeft) >= maxScroll - 8) {
        el.scrollLeft = 0;
        return;
      }
      handleLatestArticlesScroll('next');
    }, 3900);
    return () => window.clearInterval(intervalId);
  }, [handleLatestArticlesScroll, isLatestArticlesHovered, loopedLatestArticlesCards.length]);

  const getMobileCelebritySlideEls = useCallback(() => {
    const el = mobileCelebrityStripRef.current;
    if (!el) return [] as HTMLElement[];
    return Array.from(el.querySelectorAll<HTMLElement>('[data-celebrity-slide]'));
  }, []);

  const syncMobileCelebrityIndexFromScroll = useCallback(() => {
    const el = mobileCelebrityStripRef.current;
    const items = getMobileCelebritySlideEls();
    if (!el || items.length === 0) return;

    const anchorX = el.getBoundingClientRect().right - 20;
    let closest = 0;
    let minDist = Number.POSITIVE_INFINITY;
    items.forEach((child, i) => {
      const rect = child.getBoundingClientRect();
      const dist = Math.abs(rect.right - anchorX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    mobileCelebrityIndexRef.current = closest;
  }, [getMobileCelebritySlideEls]);

  const normalizeMobileCelebrityLoopScroll = useCallback(() => {
    const el = mobileCelebrityStripRef.current;
    const loopLen = celebrityLogos.length;
    const idx = mobileCelebrityIndexRef.current;
    if (!el || loopLen === 0 || idx < loopLen) return;

    const items = getMobileCelebritySlideEls();
    const active = items[idx];
    const twin = items[idx - loopLen];
    if (!active || !twin) return;

    const prevBehavior = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    el.scrollLeft += twin.offsetLeft - active.offsetLeft;
    el.style.scrollBehavior = prevBehavior || '';
    mobileCelebrityIndexRef.current = idx - loopLen;
  }, [celebrityLogos.length, getMobileCelebritySlideEls]);

  const scrollMobileCelebrityToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const el = mobileCelebrityStripRef.current;
      const items = getMobileCelebritySlideEls();
      const child = items[index];
      if (!el || !child) return;

      const prevIndex = mobileCelebrityIndexRef.current;
      const prevBehavior = el.style.scrollBehavior;
      el.style.scrollBehavior = behavior;

      if (index === prevIndex + 1 && items[prevIndex]) {
        el.scrollLeft += child.offsetLeft - items[prevIndex].offsetLeft;
      } else {
        const edgeInset = 16;
        const containerRect = el.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();
        const targetRight = containerRect.right - edgeInset;
        el.scrollBy({ left: childRect.right - targetRight, behavior: 'auto' });
      }

      el.style.scrollBehavior = prevBehavior || '';
      mobileCelebrityIndexRef.current = index;
    },
    [getMobileCelebritySlideEls],
  );

  const pauseMobileCelebrityAutoplay = useCallback(() => {
    mobileCelebrityUserInteractingRef.current = true;
    if (mobileCelebrityResumeTimerRef.current) {
      clearTimeout(mobileCelebrityResumeTimerRef.current);
    }
    mobileCelebrityResumeTimerRef.current = setTimeout(() => {
      mobileCelebrityUserInteractingRef.current = false;
    }, 5500);
  }, []);

  const handleMobileCelebrityScroll = useCallback(() => {
    if (mobileCelebrityScrollSyncTimerRef.current) {
      clearTimeout(mobileCelebrityScrollSyncTimerRef.current);
    }
    mobileCelebrityScrollSyncTimerRef.current = setTimeout(() => {
      syncMobileCelebrityIndexFromScroll();
      normalizeMobileCelebrityLoopScroll();
    }, 80);
  }, [syncMobileCelebrityIndexFromScroll, normalizeMobileCelebrityLoopScroll]);

  const handleMobileCelebrityScrollEnd = useCallback(() => {
    syncMobileCelebrityIndexFromScroll();
    normalizeMobileCelebrityLoopScroll();
  }, [syncMobileCelebrityIndexFromScroll, normalizeMobileCelebrityLoopScroll]);

  useEffect(() => {
    if (celebrityLogos.length < 2) {
      return;
    }

    const mobileMq = window.matchMedia('(max-width: 1023px)');
    const AUTO_PAUSE_MS = 4000;
    const loopLen = celebrityLogos.length;
    let initId = 0;
    let intervalId = 0;

    const startMobileAutoplay = () => {
      mobileCelebrityIndexRef.current = 0;
      initId = window.requestAnimationFrame(() => {
        scrollMobileCelebrityToIndex(0, 'auto');
      });
      intervalId = window.setInterval(() => {
        if (mobileCelebrityUserInteractingRef.current) return;
        const cur = mobileCelebrityIndexRef.current;
        const next = cur + 1;
        if (next >= loopLen * 2) return;
        scrollMobileCelebrityToIndex(next, 'smooth');
      }, AUTO_PAUSE_MS);
    };

    const stopMobileAutoplay = () => {
      window.cancelAnimationFrame(initId);
      window.clearInterval(intervalId);
      if (mobileCelebrityResumeTimerRef.current) clearTimeout(mobileCelebrityResumeTimerRef.current);
      if (mobileCelebrityScrollSyncTimerRef.current) clearTimeout(mobileCelebrityScrollSyncTimerRef.current);
    };

    const syncMobileAutoplay = () => {
      stopMobileAutoplay();
      if (mobileMq.matches) startMobileAutoplay();
    };

    syncMobileAutoplay();
    mobileMq.addEventListener('change', syncMobileAutoplay);
    return () => {
      mobileMq.removeEventListener('change', syncMobileAutoplay);
      stopMobileAutoplay();
    };
  }, [celebrityLogos.length, scrollMobileCelebrityToIndex]);

  useEffect(() => {
    if (celebrityLogos.length < 2) {
      return;
    }

    const desktopMq = window.matchMedia('(min-width: 1024px)');
    let rafId = 0;

    const startDesktopTicker = () => {
      const el = desktopCelebrityStripRef.current;
      if (!el) return;

      const speed = 0.8;
      el.scrollLeft = 0;

      const tick = () => {
        if (!isCelebrityDragging) {
          el.scrollLeft += speed;
          const loopPoint = el.scrollWidth / 2;
          if (el.scrollLeft >= loopPoint) {
            el.scrollLeft -= loopPoint;
          }
        }
        rafId = window.requestAnimationFrame(tick);
      };

      rafId = window.requestAnimationFrame(tick);
    };

    const stopDesktopTicker = () => {
      window.cancelAnimationFrame(rafId);
    };

    const syncDesktopTicker = () => {
      stopDesktopTicker();
      if (desktopMq.matches) startDesktopTicker();
    };

    syncDesktopTicker();
    desktopMq.addEventListener('change', syncDesktopTicker);
    return () => {
      desktopMq.removeEventListener('change', syncDesktopTicker);
      stopDesktopTicker();
    };
  }, [celebrityLogos.length, isCelebrityDragging]);

  // Hero slider state - use dynamic sliders from backend
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHeroPaused, setIsHeroPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const seenSlideImpressions = useRef<Set<string>>(new Set());

  const trackSliderEvent = useCallback((type: 'impression' | 'click', slideId: string, position: number) => {
    const payload = JSON.stringify({
      sliderId: slideId,
      placement: 'home_hero',
      position,
      timestamp: new Date().toISOString(),
    });

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const endpoint =
        type === 'impression'
          ? '/api/analytics/slider/impression'
          : '/api/analytics/slider/click';
      try {
        navigator.sendBeacon(endpoint, payload);
      } catch {
        // no-op: analytics failure must not break slider UX
      }
    }
  }, []);

  const heroSlides = useMemo<HeroSlide[]>(() => {
    const withRev = (path: string) => `${path}?v=${HERO_SLIDER_ASSETS_REV}`;
    const staticPaths = [
      '/assets/sliders/1.png',
      '/assets/sliders/2.png',
      '/assets/sliders/3.png',
    ];

    const fallbackSlides: HeroSlide[] = staticPaths.map((path, index) => {
      const source = withRev(path);
      const mobilePath = path.replace(/\.png$/i, 'mobile.png');
      const sourceMobile = withRev(mobilePath);
      return {
        id: `home-static-slider-${index + 1}`,
        type: 'image' as const,
        source,
        sourceMobile,
        poster: source,
        title: 'من حقیقی',
        subtitle: 'مسیر رشد و یادگیری',
        description: 'اسلایدر اصلی صفحه',
        altText: `Home slider ${index + 1}`,
        ctaLink: index === 0
          ? '/kargah-shefaye-zakhm'
          : index === 2
          ? '/courses/%D8%AF%D9%88%D8%B1%D9%87-%D8%AC%D8%A7%D9%85%D8%B9-%D8%AE%D9%84%D9%82-%D8%AB%D8%B1%D9%88%D8%AA-%D8%A8%D8%A7-%D9%85%D8%AA%D8%AF-3W'
          : undefined,
        sortOrder: index + 1,
      };
    });

    const dynamicSlides: HeroSlide[] = sliders
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((slide, index): HeroSlide | null => {
        const source = getImageUrl(slide.image);
        if (!source) return null;
        const sourceMobile = getImageUrl(slide.mobileImage) || undefined;

        return {
          id: slide.id,
          type: 'image',
          source,
          sourceMobile,
          poster: source,
          title: slide.title,
          subtitle: undefined,
          description: slide.description,
          altText: slide.title || `Home slider ${index + 1}`,
          ctaLink: slide.link || undefined,
          sortOrder: slide.order ?? index + 1,
        };
      })
      .filter((slide): slide is HeroSlide => slide !== null);

    return dynamicSlides.length > 0 ? dynamicSlides : fallbackSlides;
  }, [sliders]);

  const goToSlide = useCallback(
    (index: number) => {
      if (heroSlides.length === 0) return;
      const safeIndex = ((index % heroSlides.length) + heroSlides.length) % heroSlides.length;
      setCurrentSlide(safeIndex);
    },
    [heroSlides.length],
  );

  const goToNextSlide = useCallback(() => {
    goToSlide(currentSlide + 1);
  }, [currentSlide, goToSlide]);

  const goToPrevSlide = useCallback(() => {
    goToSlide(currentSlide - 1);
  }, [currentSlide, goToSlide]);

  // Auto-rotate hero slider
  useEffect(() => {
    if (heroSlides.length > 1 && !isHeroPaused) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }, 5500);
      return () => clearInterval(interval);
    }
  }, [heroSlides.length, isHeroPaused]);

  useEffect(() => {
    const activeSlide = heroSlides[currentSlide];
    if (!activeSlide) return;
    if (seenSlideImpressions.current.has(activeSlide.id)) return;
    seenSlideImpressions.current.add(activeSlide.id);
    trackSliderEvent('impression', activeSlide.id, currentSlide + 1);
  }, [currentSlide, heroSlides, trackSliderEvent]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (heroSlides.length <= 1) return;
      if (event.key === 'ArrowRight') goToNextSlide();
      if (event.key === 'ArrowLeft') goToPrevSlide();
      if (event.key === ' ') {
        event.preventDefault();
        setIsHeroPaused((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [goToNextSlide, goToPrevSlide, heroSlides.length]);

  const discoveryCategories = useMemo(
    () => [
      {
        key: 'courses' as const,
        title: 'دوره‌ها',
        subtitle: 'دوره‌های تخصصی و مسیرهای یادگیری ساختاریافته',
        count: Math.max(0, courseDiscoveryCategoryCount),
        href: '/courses',
        countLabel: 'دسته',
      },
      {
        key: 'articles' as const,
        title: 'مقالات',
        subtitle: 'محتوای نوشتاری تحلیلی برای رشد ذهنی',
        count: STATIC_HOME_DISCOVERY_COUNTS.articles,
        href: '/articles',
      },
      {
        key: 'podcasts' as const,
        title: 'پادکست‌ها',
        subtitle: 'یادگیری صوتی عمیق برای زمان‌های آزاد',
        count: STATIC_HOME_DISCOVERY_COUNTS.podcasts,
        href: '/podcasts',
      },
      {
        key: 'videoPodcasts' as const,
        title: 'ویدیوپادکست‌ها',
        subtitle: 'گفت‌وگوهای تصویری کاربردی و الهام‌بخش',
        count: STATIC_HOME_DISCOVERY_COUNTS.videoPodcasts,
        href: '/video-podcasts',
      },
      {
        key: 'workshops' as const,
        title: 'کارگاه‌ها',
        subtitle: 'کارگاه‌ها و رویدادهای عملی در مسیر تحول',
        count: STATIC_HOME_DISCOVERY_COUNTS.workshops,
        href: '/workshops',
      },
    ],
    [courseDiscoveryCategoryCount],
  ) as DiscoveryCategory[];

  const mobileDiscoveryLoopSlides = useMemo(
    () => [...discoveryCategories, ...discoveryCategories],
    [discoveryCategories],
  );

  const getMobileDiscoverySlideEls = useCallback(() => {
    const el = mobileDiscoveryTickerRef.current;
    if (!el) return [] as HTMLElement[];
    return Array.from(el.querySelectorAll<HTMLElement>('[data-discovery-slide]'));
  }, []);

  const syncMobileDiscoveryIndexFromScroll = useCallback(() => {
    const el = mobileDiscoveryTickerRef.current;
    const items = getMobileDiscoverySlideEls();
    if (!el || items.length === 0) return;

    const anchorX = el.getBoundingClientRect().right - 20;
    let closest = 0;
    let minDist = Number.POSITIVE_INFINITY;
    items.forEach((child, i) => {
      const rect = child.getBoundingClientRect();
      const dist = Math.abs(rect.right - anchorX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    mobileDiscoveryIndexRef.current = closest;
  }, [getMobileDiscoverySlideEls]);

  /** بعد از ورود به نیمهٔ تکراری، بدون انیمیشن به همان اسلاید در نیمهٔ اول می‌پرد (حلقهٔ بسته) */
  const normalizeMobileDiscoveryLoopScroll = useCallback(() => {
    const el = mobileDiscoveryTickerRef.current;
    const loopLen = discoveryCategories.length;
    const idx = mobileDiscoveryIndexRef.current;
    if (!el || loopLen === 0 || idx < loopLen) return;

    const items = getMobileDiscoverySlideEls();
    const active = items[idx];
    const twin = items[idx - loopLen];
    if (!active || !twin) return;

    const prevBehavior = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    el.scrollLeft += twin.offsetLeft - active.offsetLeft;
    el.style.scrollBehavior = prevBehavior || '';
    mobileDiscoveryIndexRef.current = idx - loopLen;
  }, [discoveryCategories.length, getMobileDiscoverySlideEls]);

  const scrollMobileDiscoveryToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const el = mobileDiscoveryTickerRef.current;
      const items = getMobileDiscoverySlideEls();
      const child = items[index];
      if (!el || !child) return;

      const prevIndex = mobileDiscoveryIndexRef.current;
      const prevBehavior = el.style.scrollBehavior;
      el.style.scrollBehavior = behavior;

      if (index === prevIndex + 1 && items[prevIndex]) {
        el.scrollLeft += child.offsetLeft - items[prevIndex].offsetLeft;
      } else {
        const edgeInset = 16;
        const containerRect = el.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();
        const targetRight = containerRect.right - edgeInset;
        el.scrollBy({ left: childRect.right - targetRight, behavior: 'auto' });
      }

      el.style.scrollBehavior = prevBehavior || '';
      mobileDiscoveryIndexRef.current = index;
    },
    [getMobileDiscoverySlideEls],
  );

  const pauseMobileDiscoveryAutoplay = useCallback(() => {
    mobileDiscoveryUserInteractingRef.current = true;
    if (mobileDiscoveryResumeTimerRef.current) {
      clearTimeout(mobileDiscoveryResumeTimerRef.current);
    }
    mobileDiscoveryResumeTimerRef.current = setTimeout(() => {
      mobileDiscoveryUserInteractingRef.current = false;
    }, 5500);
  }, []);

  const handleMobileDiscoveryScroll = useCallback(() => {
    if (mobileDiscoveryScrollSyncTimerRef.current) {
      clearTimeout(mobileDiscoveryScrollSyncTimerRef.current);
    }
    mobileDiscoveryScrollSyncTimerRef.current = setTimeout(() => {
      syncMobileDiscoveryIndexFromScroll();
      normalizeMobileDiscoveryLoopScroll();
    }, 80);
  }, [syncMobileDiscoveryIndexFromScroll, normalizeMobileDiscoveryLoopScroll]);

  const handleMobileDiscoveryScrollEnd = useCallback(() => {
    syncMobileDiscoveryIndexFromScroll();
    normalizeMobileDiscoveryLoopScroll();
  }, [syncMobileDiscoveryIndexFromScroll, normalizeMobileDiscoveryLoopScroll]);

  useEffect(() => {
    if (discoveryStep !== 'topics') {
      return;
    }
    if (discoveryCategories.length < 2) {
      return;
    }

    const AUTO_PAUSE_MS = 4000;
    const loopLen = discoveryCategories.length;
    mobileDiscoveryIndexRef.current = 0;

    const initId = window.requestAnimationFrame(() => {
      scrollMobileDiscoveryToIndex(0, 'auto');
    });

    const advance = () => {
      if (mobileDiscoveryUserInteractingRef.current) return;
      const cur = mobileDiscoveryIndexRef.current;
      const next = cur + 1;
      if (next >= loopLen * 2) return;
      scrollMobileDiscoveryToIndex(next, 'smooth');
    };

    const intervalId = window.setInterval(advance, AUTO_PAUSE_MS);
    return () => {
      window.cancelAnimationFrame(initId);
      window.clearInterval(intervalId);
      if (mobileDiscoveryResumeTimerRef.current) clearTimeout(mobileDiscoveryResumeTimerRef.current);
      if (mobileDiscoveryScrollSyncTimerRef.current) clearTimeout(mobileDiscoveryScrollSyncTimerRef.current);
    };
  }, [discoveryStep, discoveryCategories.length, scrollMobileDiscoveryToIndex]);

  const discoveryPreviewGroups = useMemo<DiscoveryPreviewGroup[]>(() => {
    if (activeDiscoveryCategory === 'courses') {
      return buildCourseDiscoveryPreviewGroups(courseCategoryTree);
    }

    if (activeDiscoveryCategory === 'podcasts') {
      return MOCK_PODCAST_COLUMNS.map((column) => ({
        id: column.id,
        title: column.title,
        subtitle: column.subtitle,
        items: column.items.map((item) => ({
          id: item.id,
          label: item.label,
          href: item.href,
          badge: item.badge,
        })),
      }));
    }

    if (activeDiscoveryCategory === 'articles') {
      return [
        {
          id: 'articles-latest',
          title: 'مقالات منتخب',
          subtitle: 'نمایش سریع از جدیدترین مقاله‌ها',
          items: (articlesList.length > 0 ? articlesList : articles)
            .slice(0, 3)
            .map((item) => ({
              id: item.id,
              label: item.title,
              href: `/articles/${item.slug}`,
            })),
        },
      ];
    }

    if (activeDiscoveryCategory === 'videoPodcasts') {
      return [
        {
          id: 'video-podcasts-latest',
          title: 'ویدیوپادکست‌های منتخب',
          subtitle: 'اپیزودهای تصویری اخیر',
          items: videoPodcasts.slice(0, 3).map((item) => ({
            id: item.id,
            label: item.title,
            href: '/video-podcasts',
          })),
        },
      ];
    }

    return [
      {
        id: 'workshops-latest',
        title: 'کارگاه‌های منتخب',
        subtitle: 'کارگاه‌ها و رویدادهای پیشنهادی',
        items: workshops.slice(0, 3).map((item) => ({
          id: item.id,
          label: item.title,
          href: `/workshops/${item.id}`,
        })),
      },
    ];
  }, [activeDiscoveryCategory, articles, articlesList, courseCategoryTree, videoPodcasts, workshops]);

  // Global audio player (bottom bar)
  const {
    playPodcast,
    closePlayer,
    useFloatingPlayerChrome,
    setUseFloatingPlayerChrome,
    togglePlayPause,
    currentPodcast,
    isPlaying,
    currentTime,
    duration,
    seek,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayer();

  // Parallax refs for sections
  const podcastSectionRef = useRef<HTMLDivElement>(null);
  const mentorSectionRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<any>(null);
  const [places, setPlaces] = useState<any[]>([]);
  
  const { scrollYProgress: podcastScrollProgress } = useScroll();

  const { scrollYProgress: mentorScrollProgress } = useScroll();

  const podcastY1 = useTransform(podcastScrollProgress, [0, 1], ['0%', '30%']);
  const podcastY2 = useTransform(podcastScrollProgress, [0, 1], ['0%', '-30%']);
  const podcastOpacity = useTransform(podcastScrollProgress, [0, 0.5, 1], [0.3, 1, 0.3]);

  const mentorY1 = useTransform(mentorScrollProgress, [0, 1], ['0%', '30%']);
  const mentorY2 = useTransform(mentorScrollProgress, [0, 1], ['0%', '-30%']);
  const mentorOpacity = useTransform(mentorScrollProgress, [0, 0.5, 1], [0.3, 1, 0.3]);

  // Load populated places for globe labels (similar to official react-globe.gl example)
  useEffect(() => {
    fetch(
      '//unpkg.com/three-globe@2.36.0/example/datasets/ne_110m_populated_places_simple.geojson'
    )
      .then((res) => res.json())
      .then(({ features }) => {
        setPlaces(features || []);
      })
      .catch(() => {
        // Fallback: a few manual points
        setPlaces([
          {
            properties: {
              name: 'Tehran',
              latitude: 35.6892,
              longitude: 51.389,
              pop_max: 13532000,
            },
          },
          {
            properties: {
              name: 'Los Angeles',
              latitude: 34.0522,
              longitude: -118.2437,
              pop_max: 12750807,
            },
          },
          {
            properties: {
              name: 'London',
              latitude: 51.5074,
              longitude: -0.1278,
              pop_max: 9126366,
            },
          },
          {
            properties: {
              name: 'Tokyo',
              latitude: 35.6762,
              longitude: 139.6503,
              pop_max: 37435191,
            },
          },
        ]);
      });
  }, []);

  // Audio player handlers (hooked to global bar)
  const handlePlayPause = (podcast: Podcast) => {
    const audioUrl = podcast.streamUrl || null;
    if (!audioUrl) {
      alert('فایل صوتی در دسترس نیست');
      return;
    }

    if (currentPodcast?.id === podcast.id) {
      togglePlayPause();
    } else {
      playPodcast(podcast);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentPodcast) return;
    const percent = parseFloat(e.target.value);
    const newTime = (percent / 100) * (duration || 0);
    seek(newTime);
  };

  const handleJumpBackward = () => {
    if (!currentPodcast) return;
    seek(Math.max(0, currentTime - 30));
  };

  const handleJumpForward = () => {
    if (!currentPodcast) return;
    seek(Math.min(duration, currentTime + 30));
  };

  const handlePrevious = () => {
    if (featuredPodcasts.length === 0) return;
    const currentId = currentPodcast?.id;
    const currentIndex = currentId ? featuredPodcasts.findIndex((p) => p.id === currentId) : 0;
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;
    const prevIndex = safeIndex > 0 ? safeIndex - 1 : featuredPodcasts.length - 1;
    handlePlayPause(featuredPodcasts[prevIndex]);
  };

  const handleNext = () => {
    if (featuredPodcasts.length === 0) return;
    const currentId = currentPodcast?.id;
    const currentIndex = currentId ? featuredPodcasts.findIndex((p) => p.id === currentId) : 0;
    const safeIndex = currentIndex === -1 ? 0 : currentIndex;
    const nextIndex = (safeIndex + 1) % featuredPodcasts.length;
    handlePlayPause(featuredPodcasts[nextIndex]);
  };

  const handlePodcastCardPlayPause = (podcast: Podcast) => {
    handlePlayPause(podcast);
    setUseFloatingPlayerChrome(true);
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePrimaryCta = () => {
    if (primaryWorkshop) {
      onOpenPreRegister(primaryWorkshop);
      return;
    }
    navigate('/workshops');
  };

  const [selectedVideoPodcast, setSelectedVideoPodcast] = useState<VideoPodcast | null>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const handleVideoPodcastClick = (video: VideoPodcast) => {
    setSelectedVideoPodcast(video);
    setIsVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideoPodcast(null);
  };

  const renderSectionTitle = (title: string) => {
    const words = title.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return <h2 className="mt-1 text-3xl font-black text-[#0f172a] sm:mt-2 sm:text-4xl" />;
    }

    if (words.length === 1) {
      const word = words[0];
      const tarinIndex = word.lastIndexOf('ترین');
      const splitIndex = tarinIndex > 0 ? tarinIndex : Math.ceil(word.length / 2);
      return (
        <h2 className="mt-1 text-3xl font-black text-[#0f172a] sm:mt-2 sm:text-4xl">
          <span>{word.slice(0, splitIndex)}</span>
          <span className="text-[#7c3aed]">{word.slice(splitIndex)}</span>
        </h2>
      );
    }

    const prefix = words.slice(0, -1).join(' ');
    const lastWord = words[words.length - 1];
    return (
      <h2 className="mt-1 text-3xl font-black text-[#0f172a] sm:mt-2 sm:text-4xl">
        <span>{prefix} </span>
        <span className="text-[#7c3aed]">{lastWord}</span>
      </h2>
    );
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-[#f8fafc] via-[#eef2ff] to-[#f8fafc] text-white" dir="rtl">
      {/* Hero Slider Section */}
      <section
        className="relative"
        aria-roledescription="carousel"
        aria-label="اسلایدر صفحه اصلی"
        onMouseEnter={() => setIsHeroPaused(true)}
        onMouseLeave={() => setIsHeroPaused(false)}
      >
        {/*
          ارتفاع این باکس را تصویر «شبح» پایین تعیین می‌کند تا دقیقاً نسبت تصویر اسلاید باشد.
          min-height ثابت (۲۴۰px) روی موبایل باکس را از تصویر بلندتر می‌کرد و object-cover
          دو طرف اسلاید را می‌برید؛ به همین دلیل حذف شد.
        */}
        <div
          className="relative overflow-hidden bg-[#f8fafc]"
          onTouchStart={(event) => setTouchStartX(event.changedTouches[0]?.clientX ?? null)}
          onTouchEnd={(event) => {
            if (touchStartX === null || heroSlides.length <= 1) return;
            const deltaX = event.changedTouches[0].clientX - touchStartX;
            if (Math.abs(deltaX) > 45) {
              if (deltaX < 0) goToNextSlide();
              if (deltaX > 0) goToPrevSlide();
            }
            setTouchStartX(null);
          }}
        >
          {/* تصویرهای «شبح» — ارتفاع باکس را برابر بلندترین اسلاید می‌کنند.
              هر کدام همان منبعی را می‌گیرند که واقعاً نمایش داده می‌شود (موبایل/دسکتاپ)
              تا نسبت تصویر روی موبایل هم درست محاسبه شود. */}
          <div className="grid w-full">
            {heroSlides.map((slide, index) => (
              <picture
                key={`${slide.id || index}-ghost`}
                className="block w-full"
                style={{ gridArea: '1 / 1' }}
              >
                {slide.sourceMobile && (
                  <source media="(max-width: 1023px)" srcSet={slide.sourceMobile} type="image/png" />
                )}
                <img src={slide.source} className="w-full h-auto block invisible" alt="" aria-hidden />
              </picture>
            ))}
          </div>
          <div className="absolute inset-0">
            {/* Slider Background Images */}
            {heroSlides.map((slide, index) => {
              const slideSrc = slide.source;
              const isVideo = slide.type === 'video';
              const slidePoster = slide.poster;
              const uniqueKey = slide.id || slideSrc || `slide-${index}`;

              return (
                <motion.div
                  key={uniqueKey}
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: currentSlide === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.28, ease: 'easeOut' }}
                  className="absolute inset-0"
                  onClick={slide.ctaLink ? () => navigate(slide.ctaLink!) : undefined}
                  role={slide.ctaLink ? 'link' : undefined}
                  aria-label={slide.ctaLink ? slide.altText : undefined}
                  style={{
                    pointerEvents: currentSlide === index ? 'auto' : 'none',
                    cursor: slide.ctaLink ? 'pointer' : 'default',
                  }}
                >
                  {isVideo && slideSrc ? (
                    <video
                      key={`${uniqueKey}-video`}
                      src={slideSrc}
                      poster={slidePoster}
                      className="h-full w-full object-cover object-center"
                      autoPlay={currentSlide === index}
                      loop
                      muted
                      playsInline
                      preload={index === 0 ? 'metadata' : 'none'}
                    />
                  ) : slideSrc ? (
                    slide.sourceMobile ? (
                      <picture key={`${uniqueKey}-pic`} className="block h-full w-full">
                        <source media="(max-width: 1023px)" srcSet={slide.sourceMobile} type="image/png" />
                        <img
                          key={`${uniqueKey}-img`}
                          src={slideSrc}
                          alt={slide.altText || `Hero slide ${index + 1}`}
                          /* contain تا هیچ اسلایدی — با هر نسبت تصویری — از دو طرف بریده نشود */
                          className="h-full w-full object-contain object-center"
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchPriority={index === 0 ? 'high' : 'auto'}
                          sizes="100vw"
                        />
                      </picture>
                    ) : (
                      <img
                        key={`${uniqueKey}-img`}
                        src={slideSrc}
                        alt={slide.altText || `Hero slide ${index + 1}`}
                        className="h-full w-full object-contain object-center"
                        loading={index === 0 ? 'eager' : 'lazy'}
                        fetchPriority={index === 0 ? 'high' : 'auto'}
                        sizes="100vw"
                      />
                    )
                  ) : null}
                </motion.div>
              );
            })}
            {heroSlides.length > 1 ? (
              <div className="pointer-events-none absolute inset-0 z-10 hidden lg:block">
                <div className="pointer-events-auto absolute bottom-4 left-1/2 z-20 -translate-x-1/2 rounded-full border border-white/25 bg-black/25 px-3 py-2 backdrop-blur-md sm:bottom-6 sm:px-3.5">
                  <div className="flex items-center gap-1.5">
                    {heroSlides.map((slide, index) => {
                      const uniqueKey = slide.id || slide.source || `indicator-${index}`;
                      return (
                        <button
                          key={uniqueKey}
                          type="button"
                          onClick={() => goToSlide(index)}
                          className={`rounded-full transition-all duration-200 ${
                            currentSlide === index
                              ? 'h-2 w-5 bg-white'
                              : 'h-2 w-2 bg-white/45 hover:bg-white/70'
                          }`}
                          aria-label={`Go to slide ${index + 1}`}
                          aria-current={currentSlide === index}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="pointer-events-auto absolute bottom-4 right-4 z-20 flex items-center gap-2 rounded-2xl border border-white/30 bg-black/25 p-2 backdrop-blur-md sm:bottom-6 sm:right-6 sm:gap-3 sm:p-2.5">
                  <button
                    type="button"
                    onClick={goToPrevSlide}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/35 bg-white/90 text-[#1f2a44] shadow-[0_8px_20px_-14px_rgba(0,0,0,0.45)] transition-all duration-200 hover:scale-105 hover:bg-white sm:h-10 sm:w-10"
                    aria-label="اسلاید قبلی"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <button
                    type="button"
                    onClick={goToNextSlide}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/35 bg-white/90 text-[#1f2a44] shadow-[0_8px_20px_-14px_rgba(0,0,0,0.45)] transition-all duration-200 hover:scale-105 hover:bg-white sm:h-10 sm:w-10"
                    aria-label="اسلاید بعدی"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {heroSlides.length > 1 ? (
          <div className="flex justify-center bg-[#f8fafc] py-2.5 lg:hidden">
            <div className="flex items-center gap-1.5 rounded-full border border-white/25 bg-black/25 px-3 py-2 backdrop-blur-md">
              {heroSlides.map((slide, index) => {
                const uniqueKey = slide.id || slide.source || `indicator-mobile-${index}`;
                return (
                  <button
                    key={uniqueKey}
                    type="button"
                    onClick={() => goToSlide(index)}
                    className={`rounded-full transition-all duration-200 ${
                      currentSlide === index
                        ? 'h-2 w-5 bg-white'
                        : 'h-2 w-2 bg-white/45 hover:bg-white/70'
                    }`}
                    aria-label={`رفتن به اسلاید ${index + 1}`}
                    aria-current={currentSlide === index}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </section>
      <main className="mx-auto w-full min-w-0 max-w-6xl overflow-x-hidden px-4 pb-8 sm:px-8 sm:pb-16">
        {/* Discovery by category */}
        <section className="relative mx-[calc(50%-50vw)] w-screen max-w-[100vw] overflow-x-hidden border-t-0 py-4 sm:py-8 lg:mx-0 lg:w-auto lg:max-w-none lg:overflow-visible lg:border-t lg:border-slate-200/70">

          <div className="relative min-w-0 px-4 sm:px-8 lg:px-3 xl:px-4">
            <div className="mb-3 flex items-center justify-center gap-3 sm:justify-between">
              <div className="text-center sm:text-right">
                <h2 className="text-lg font-semibold tracking-tight text-[#0f172a] sm:mt-2 sm:text-3xl">
                  <span>انتخاب بر اساس </span>
                  <span className="text-[#7c3aed]">دسته‌بندی</span>
                </h2>
              </div>
            </div>

            <div className="overflow-hidden rounded-[28px] bg-transparent lg:overflow-visible">
              <div className="px-4 py-3 sm:px-5">
                {discoveryStep === 'topics' ? (
                  null
                ) : (
                  <button
                    type="button"
                    onClick={() => setDiscoveryStep('topics')}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1C3B9B] transition-opacity hover:opacity-80"
                  >
                    <ArrowRight className="h-4 w-4" />
                    <span>بازگشت به موضوعات</span>
                  </button>
                )}
              </div>

              <div className="p-3 sm:p-4 lg:px-2 lg:py-3">
                <AnimatePresence mode="wait">
                  {discoveryStep === 'topics' ? (
                    <motion.div
                      key="discovery-topics"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ duration: 0.24, ease: 'easeOut' }}
                      className="space-y-3"
                    >
                      <div className="hidden min-w-0 lg:-mx-10 lg:w-[calc(100%+5rem)] lg:grid lg:grid-cols-5 lg:items-stretch lg:gap-4 xl:-mx-16 xl:w-[calc(100%+8rem)] xl:gap-5 2xl:-mx-20 2xl:w-[calc(100%+10rem)]">
                        {discoveryCategories.map((category) => (
                          <div key={`${category.key}-desktop`} className="min-w-0">
                            <DiscoveryCategoryCard
                              category={category}
                              onOpen={(key) => {
                                setActiveDiscoveryCategory(key);
                                setDiscoveryStep('subcategories');
                              }}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="min-w-0 max-w-full lg:hidden">
                        <div className="min-w-0 overflow-hidden rounded-2xl py-2">
                          <div
                            ref={mobileDiscoveryTickerRef}
                            className="w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain scroll-smooth py-2 [-ms-overflow-style:none] [scroll-padding-inline:1rem] [scrollbar-width:none] [touch-action:pan-x] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden"
                            onTouchStart={pauseMobileDiscoveryAutoplay}
                            onTouchEnd={() => {
                              pauseMobileDiscoveryAutoplay();
                              handleMobileDiscoveryScrollEnd();
                            }}
                            onScroll={handleMobileDiscoveryScroll}
                            onScrollEnd={handleMobileDiscoveryScrollEnd}
                          >
                            <div
                              dir="rtl"
                              className="flex w-max min-w-full snap-x snap-mandatory gap-3 ps-4 pe-4"
                            >
                            {mobileDiscoveryLoopSlides.map((category, slideIndex) => (
                              <button
                                key={`${category.key}-loop-${slideIndex}`}
                                type="button"
                                data-discovery-slide
                                data-slide-index={slideIndex % discoveryCategories.length}
                                onClick={() => navigate(category.href)}
                                className="group box-border inline-flex w-[7.5rem] max-w-[calc(100vw-2.5rem)] shrink-0 snap-start flex-col items-center gap-2 rounded-2xl bg-transparent px-1 py-1 sm:w-[8rem]"
                                aria-label={category.title}
                              >
                                <div className="box-border size-[5.7rem] shrink-0 rounded-full border-[4px] border-[#7c3aed] bg-white p-2 shadow-[0_18px_28px_-18px_rgba(124,58,237,0.55)]">
                                  <img
                                    src={DISCOVERY_CATEGORY_IMAGE_MAP[category.key]}
                                    alt={category.title}
                                    className="block h-full w-full rounded-full object-cover transition duration-300 group-active:scale-95"
                                    loading="lazy"
                                  />
                                </div>
                                <p className="line-clamp-2 min-h-[2rem] w-full text-center text-xs font-black leading-5 text-[#0f172a]">
                                  {category.title}
                                </p>
                              </button>
                            ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`discovery-subcategories-${activeDiscoveryCategory}`}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.24, ease: 'easeOut' }}
                    >
                      {activeDiscoveryCategory === 'courses' ? (
                        <CourseDiscoveryCoursesPanel groups={discoveryPreviewGroups} />
                      ) : (
                        <>
                          <div className="mb-4 flex items-center justify-between pb-3">
                            <h3 className="text-sm font-extrabold text-slate-900 sm:text-base">
                              {activeDiscoveryCategory === 'podcasts'
                                ? 'دسته‌بندی پادکست‌ها'
                                : 'دسته‌بندی‌های این موضوع'}
                            </h3>
                            <span className="rounded-full bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-2.5 py-0.5 text-[10px] font-bold text-white shadow-[0_10px_18px_-12px_rgba(28,59,155,0.6)]">
                              نمونه
                            </span>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {discoveryPreviewGroups.map((group) => (
                              <div
                                key={group.id}
                                className="rounded-[28px] border border-slate-200 bg-white/92 p-4 text-right shadow-[0_12px_24px_-20px_rgba(15,23,42,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#1C3B9B]/25 hover:shadow-[0_18px_34px_-20px_rgba(28,59,155,0.28)]"
                              >
                                <p className="text-sm font-black text-slate-900">{group.title}</p>
                                <p className="mt-1 text-xs text-slate-500">{group.subtitle}</p>
                                <div className="mt-3 space-y-2">
                                  {group.items.length > 0 ? (
                                    group.items.map((item) => (
                                      <button
                                        key={`${group.id}-${item.id}`}
                                        type="button"
                                        onClick={() => navigate(item.href)}
                                        className="group flex w-full items-center justify-between rounded-full border border-slate-200 bg-white px-3 py-2.5 text-right text-xs text-slate-700 transition-all duration-300 hover:border-[#1C3B9B]/25 hover:bg-gradient-to-l hover:from-[#1C3B9B]/[0.06] hover:to-[#C11277]/[0.06] hover:shadow-[0_12px_24px_-20px_rgba(28,59,155,0.45)] hover:text-[#1C3B9B]"
                                      >
                                        <span className="line-clamp-1">{item.label}</span>
                                        <span className="inline-flex items-center gap-1">
                                          {item.badge ? (
                                            <span className="rounded-full bg-gradient-to-l from-[#1C3B9B] to-[#C11277] px-1.5 py-0.5 text-[10px] font-bold text-white">
                                              {item.badge}
                                            </span>
                                          ) : null}
                                          <ArrowLeft className="h-3.5 w-3.5 text-slate-400 transition-all duration-300 group-hover:-translate-x-0.5 group-hover:text-[#1C3B9B]" />
                                        </span>
                                      </button>
                                    ))
                                  ) : (
                                    <span className="text-xs text-slate-500">موردی موجود نیست</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>

        {/* Newest Courses Strip */}
        <HomeCourseCarouselSection
          eyebrow="تازه‌ها"
          titlePrefix="جدیدترین"
          titleAccent="ها"
          loopedCards={loopedNewestCourseCards}
          categoryTabs={newestCategoryTabs}
          activeTab={newestActiveTab}
          onTabChange={setNewestActiveTab}
          carouselRef={newestCarouselRef}
          onScroll={handleNewestCarouselScroll}
          onMouseEnter={() => setIsNewestCarouselHovered(true)}
          onMouseLeave={() => setIsNewestCarouselHovered(false)}
          navigate={navigate}
        />

        {/* Popular Courses Strip */}
        <section dir="rtl" className="border-t border-slate-200/70 px-2 py-4 sm:px-0 sm:py-8">
          <div className="mb-3 flex items-end justify-between gap-2">
            <div className="text-right">
              <p className="hidden text-sm font-semibold tracking-[0.28em] text-[#1C3B9B] sm:block">دوره‌های منتخب</p>
              <h2 className="text-[1.75rem] font-black leading-tight text-[#0f172a] sm:mt-2 sm:text-4xl">
                <span>پرطرفدارترین </span>
                <span className="text-[#7c3aed]">ها</span>
              </h2>
            </div>
            <div className="mb-3 hidden h-px flex-1 bg-gradient-to-l from-slate-300/20 via-slate-300/55 to-transparent sm:block" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePopularCarouselScroll('prev')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید قبلی"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handlePopularCarouselScroll('next')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید بعدی"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/courses')}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#475569] transition hover:text-[#1C3B9B] sm:gap-2 sm:text-sm"
              >
                <span>همه دوره‌ها</span>
                <ArrowLeft className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4 flex flex-nowrap items-center justify-start gap-2 overflow-x-auto p-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:mb-6 sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-[#eef2ff]/70 sm:p-2">
            {popularCategoryTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setPopularActiveTab(tab.key)}
                className={`flex-none whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition sm:rounded-xl sm:px-3.5 sm:py-2 sm:text-sm ${
                  popularActiveTab === tab.key
                    ? 'border-black bg-black text-white sm:border-transparent sm:bg-white sm:text-[#1C3B9B] sm:shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-[#1C3B9B] sm:border-transparent sm:bg-transparent sm:hover:bg-white/70'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loopedPopularCourseCards.length > 0 ? (
            <div
              className="relative"
              onMouseEnter={() => setIsPopularCarouselHovered(true)}
              onMouseLeave={() => setIsPopularCarouselHovered(false)}
            >
              <div
                ref={popularCarouselRef}
                className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 pb-3 scroll-smooth hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-5 sm:px-1"
                dir="rtl"
              >
                {loopedPopularCourseCards.map((course, index) => (
                  <motion.button
                    key={course.id}
                    type="button"
                    onClick={() => navigate(`/courses/${course.pathSegment}`)}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.45, delay: index * 0.04 }}
                    whileHover={{ y: -6, scale: 1.005 }}
                    className="group relative min-w-[52vw] max-w-[52vw] snap-start overflow-hidden rounded-[14px] border border-slate-200/90 bg-white text-right shadow-[0_12px_20px_-18px_rgba(15,23,42,0.26)] transition hover:border-[#1C3B9B]/25 sm:min-w-[280px] sm:max-w-[280px] sm:rounded-[20px]"
                  >
                    <div className="relative h-28 overflow-hidden rounded-t-[14px] sm:h-36 sm:rounded-t-[20px]">
                      <img
                        src={course.thumbnail}
                        alt={course.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading={index < 3 ? 'eager' : 'lazy'}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                      {course.discount > 0 ? (
                        <span className="absolute right-2 top-2 z-20 rounded-full bg-[#dc2626] px-2.5 py-1 text-[10px] font-bold text-white shadow-[0_10px_18px_-10px_rgba(220,38,38,0.65)]">
                          %{course.discount}
                        </span>
                      ) : null}
                    </div>

                    <div className="grid min-h-[176px] grid-rows-[2.6rem_1.2rem_1.2rem_1.2rem_1fr] gap-1.5 p-2.5 sm:min-h-[182px] sm:grid-rows-[3rem_1.25rem_1.25rem_1fr] sm:gap-2 sm:p-4">
                      <h3 className="line-clamp-2 h-[2.6rem] text-[15px] font-extrabold leading-5 text-[#0f172a] group-hover:text-[#1C3B9B] sm:h-12 sm:text-base">
                        {course.title}
                      </h3>

                      <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] sm:h-5 sm:text-[11px]">
                        <span className="inline-flex min-w-0 items-center gap-1 text-amber-500">
                          <Star className="h-3.5 w-3.5 fill-current" />
                          <span className="truncate font-bold">
                            {course.rating > 0 ? course.rating.toLocaleString('fa-IR') : '—'}
                          </span>
                        </span>
                        <span className="truncate text-slate-400">امتیاز کاربران</span>
                      </div>

                      <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-slate-500 sm:h-5 sm:text-[11px]">
                        <span className="inline-flex min-w-0 items-center gap-1">
                          <UserRound className="h-3.5 w-3.5 text-slate-400" />
                          <span className="truncate">{course.instructor}</span>
                        </span>
                        <span className="inline-flex min-w-0 items-center gap-1">
                          <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                          <span className="truncate">{course.duration}</span>
                        </span>
                      </div>

                      <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-[#64748b] sm:h-5 sm:text-[11px]">
                        <span className="truncate">{course.lessonsCount.toLocaleString('fa-IR')} جلسه</span>
                        <span className="inline-flex min-w-0 items-center gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#C11277]" />
                          <span className="truncate">آنلاین</span>
                        </span>
                      </div>

                      <div className="mt-auto flex h-auto items-end justify-between gap-1">
                        <div className="flex min-h-[2.2rem] flex-col items-start justify-end text-right sm:min-h-10">
                          {course.oldPrice > 0 && course.price > 0 ? (
                            <p className="text-[11px] font-medium text-slate-400 line-through decoration-1 decoration-red-400/80">
                              {course.oldPrice.toLocaleString('fa-IR')} تومان
                            </p>
                          ) : (
                            <p className="text-[11px] text-transparent">-</p>
                          )}
                          {course.price > 0 ? (
                            <p className="text-[15px] font-black text-[#0f172a] sm:text-sm">
                              {course.price.toLocaleString('fa-IR')} تومان
                            </p>
                          ) : (
                            <FreeBadge size="sm" className="shadow-none" />
                          )}
                        </div>
                        <div className="flex min-w-0 items-center gap-1 text-[11px] font-bold text-[#1C3B9B] sm:gap-1.5 sm:text-xs">
                          <span className="truncate sm:hidden">مشاهده</span>
                          <span className="hidden truncate sm:inline">مشاهده جزئیات</span>
                          <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-[#64748b]">
              دوره‌ای برای نمایش وجود ندارد.
            </div>
          )}
        </section>

        {/* Best Selling Courses Strip */}
        <section dir="rtl" className="border-t border-slate-200/70 py-4 sm:py-8">
          <div className="mb-3 flex items-end justify-between gap-2">
            <div className="text-right">
              <p className="hidden text-sm font-semibold tracking-[0.28em] text-[#1C3B9B] sm:block">انتخاب کاربران</p>
              {renderSectionTitle('پرفروش ترین ها')}
            </div>
            <div className="mb-3 hidden h-px flex-1 bg-gradient-to-l from-slate-300/20 via-slate-300/55 to-transparent sm:block" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleBestSellingCarouselScroll('prev')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید قبلی"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleBestSellingCarouselScroll('next')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید بعدی"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/courses')}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#475569] transition hover:text-[#1C3B9B] sm:gap-2 sm:text-sm"
              >
                <span>همه دوره‌ها</span>
                <ArrowLeft className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4 flex flex-nowrap items-center justify-start gap-2 overflow-x-auto p-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:mb-6 sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-[#eef2ff]/70 sm:p-2">
            {bestSellingCategoryTabs.map((tab) => (
              <button
                key={`best-selling-${tab.key}`}
                type="button"
                onClick={() => setBestSellingActiveTab(tab.key)}
                className={`flex-none whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition sm:rounded-xl sm:px-3.5 sm:py-2 sm:text-sm ${
                  bestSellingActiveTab === tab.key
                    ? 'border-black bg-black text-white sm:border-transparent sm:bg-white sm:text-[#1C3B9B] sm:shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-[#1C3B9B] sm:border-transparent sm:bg-transparent sm:hover:bg-white/70'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setIsBestSellingHovered(true)}
            onMouseLeave={() => setIsBestSellingHovered(false)}
          >
            <div
              ref={bestSellingCarouselRef}
              className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 pb-3 scroll-smooth hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-5 sm:px-1"
              dir="rtl"
            >
              {loopedBestSellingCards.map((course, index) => (
                <motion.button
                  key={course.id}
                  type="button"
                  onClick={() => navigate(`/courses/${course.pathSegment}`)}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.45, delay: index * 0.04 }}
                  whileHover={{ y: -6, scale: 1.005 }}
                  className="group relative min-w-[52vw] max-w-[52vw] snap-start overflow-hidden rounded-[14px] border border-slate-200/90 bg-white text-right shadow-[0_12px_20px_-18px_rgba(15,23,42,0.26)] transition hover:border-[#1C3B9B]/25 sm:min-w-[280px] sm:max-w-[280px] sm:rounded-[20px]"
                >
                  <div className="relative h-28 overflow-hidden rounded-t-[14px] sm:h-36 sm:rounded-t-[20px]">
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading={index < 3 ? 'eager' : 'lazy'}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                    {course.discount > 0 ? (
                      <span className="absolute right-2 top-2 z-20 rounded-full bg-[#dc2626] px-2.5 py-1 text-[10px] font-bold text-white shadow-[0_10px_18px_-10px_rgba(220,38,38,0.65)]">
                        %{course.discount}
                      </span>
                    ) : null}
                  </div>

                  <div className="grid min-h-[176px] grid-rows-[2.6rem_1.2rem_1.2rem_1.2rem_1fr] gap-1.5 p-2.5 sm:min-h-[182px] sm:grid-rows-[3rem_1.25rem_1.25rem_1fr] sm:gap-2 sm:p-4">
                    <h3 className="line-clamp-2 h-[2.6rem] text-[15px] font-extrabold leading-5 text-[#0f172a] group-hover:text-[#1C3B9B] sm:h-12 sm:text-base">
                      {course.title}
                    </h3>

                    <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] sm:h-5 sm:text-[11px]">
                      <span className="inline-flex min-w-0 items-center gap-1 text-amber-500">
                        <Star className="h-3.5 w-3.5 fill-current" />
                        <span className="truncate font-bold">
                          {course.rating > 0 ? course.rating.toLocaleString('fa-IR') : '—'}
                        </span>
                      </span>
                      <span className="truncate text-slate-400">امتیاز کاربران</span>
                    </div>

                    <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-slate-500 sm:h-5 sm:text-[11px]">
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <UserRound className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">{course.instructor}</span>
                      </span>
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">{course.duration}</span>
                      </span>
                    </div>

                    <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-[#64748b] sm:h-5 sm:text-[11px]">
                      <span className="truncate">{course.lessonsCount.toLocaleString('fa-IR')} جلسه</span>
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#C11277]" />
                        <span className="truncate">آنلاین</span>
                      </span>
                    </div>

                    <div className="mt-auto flex h-auto items-end justify-between gap-1">
                      <div className="flex min-h-[2.2rem] flex-col items-start justify-end text-right sm:min-h-10">
                        {course.oldPrice > 0 && course.price > 0 ? (
                          <p className="text-[11px] font-medium text-slate-400 line-through decoration-1 decoration-red-400/80">
                            {course.oldPrice.toLocaleString('fa-IR')} تومان
                          </p>
                        ) : (
                          <p className="text-[11px] text-transparent">-</p>
                        )}
                        {course.price > 0 ? (
                          <p className="text-[15px] font-black text-[#0f172a] sm:text-sm">
                            {course.price.toLocaleString('fa-IR')} تومان
                          </p>
                        ) : (
                          <FreeBadge size="sm" className="shadow-none" />
                        )}
                      </div>
                      <div className="flex min-w-0 items-center gap-1 text-[11px] font-bold text-[#1C3B9B] sm:gap-1.5 sm:text-xs">
                        <span className="truncate sm:hidden">مشاهده</span>
                        <span className="hidden truncate sm:inline">مشاهده جزئیات</span>
                        <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* Free Courses Strip */}
        <section dir="rtl" className="border-t border-slate-200/70 py-4 sm:py-8">
          <div className="mb-3 flex items-end justify-between gap-2">
            <div className="text-right">
              <p className="hidden text-sm font-semibold tracking-[0.28em] text-[#1C3B9B] sm:block">بدون هزینه شروع کن</p>
              {renderSectionTitle('دوره های رایگان')}
            </div>
            <div className="mb-3 hidden h-px flex-1 bg-gradient-to-l from-slate-300/20 via-slate-300/55 to-transparent sm:block" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleFreeCarouselScroll('prev')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید قبلی"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleFreeCarouselScroll('next')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید بعدی"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/courses')}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#475569] transition hover:text-[#1C3B9B] sm:gap-2 sm:text-sm"
              >
                <span>همه دوره‌ها</span>
                <ArrowLeft className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4 flex flex-nowrap items-center justify-start gap-2 overflow-x-auto p-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:mb-6 sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-[#eef2ff]/70 sm:p-2">
            {freeCategoryTabs.map((tab) => (
              <button
                key={`free-${tab.key}`}
                type="button"
                onClick={() => setFreeActiveTab(tab.key)}
                className={`flex-none whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition sm:rounded-xl sm:px-3.5 sm:py-2 sm:text-sm ${
                  freeActiveTab === tab.key
                    ? 'border-black bg-black text-white sm:border-transparent sm:bg-white sm:text-[#1C3B9B] sm:shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-[#1C3B9B] sm:border-transparent sm:bg-transparent sm:hover:bg-white/70'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setIsFreeCarouselHovered(true)}
            onMouseLeave={() => setIsFreeCarouselHovered(false)}
          >
            <div
              ref={freeCarouselRef}
              className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 pb-3 scroll-smooth hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-5 sm:px-1"
              dir="rtl"
            >
              {loopedFreeCards.map((course, index) => (
                <motion.button
                  key={course.id}
                  type="button"
                  onClick={() => navigate(`/courses/${course.pathSegment}`)}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.45, delay: index * 0.04 }}
                  whileHover={{ y: -6, scale: 1.005 }}
                  className="group relative min-w-[52vw] max-w-[52vw] snap-start overflow-hidden rounded-[14px] border border-slate-200/90 bg-white text-right shadow-[0_12px_20px_-18px_rgba(15,23,42,0.26)] transition hover:border-[#1C3B9B]/25 sm:min-w-[280px] sm:max-w-[280px] sm:rounded-[20px]"
                >
                  <div className="relative h-28 overflow-hidden rounded-t-[14px] sm:h-36 sm:rounded-t-[20px]">
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading={index < 3 ? 'eager' : 'lazy'}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                    {course.discount > 0 ? (
                      <span className="absolute right-2 top-2 z-20 rounded-full bg-[#dc2626] px-2.5 py-1 text-[10px] font-bold text-white shadow-[0_10px_18px_-10px_rgba(220,38,38,0.65)]">
                        %{course.discount}
                      </span>
                    ) : null}
                  </div>

                  <div className="grid min-h-[176px] grid-rows-[2.6rem_1.2rem_1.2rem_1.2rem_1fr] gap-1.5 p-2.5 sm:min-h-[182px] sm:grid-rows-[3rem_1.25rem_1.25rem_1fr] sm:gap-2 sm:p-4">
                    <h3 className="line-clamp-2 h-[2.6rem] text-[15px] font-extrabold leading-5 text-[#0f172a] group-hover:text-[#1C3B9B] sm:h-12 sm:text-base">
                      {course.title}
                    </h3>

                    <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] sm:h-5 sm:text-[11px]">
                      <span className="inline-flex min-w-0 items-center gap-1 text-amber-500">
                        <Star className="h-3.5 w-3.5 fill-current" />
                        <span className="truncate font-bold">
                          {course.rating > 0 ? course.rating.toLocaleString('fa-IR') : '—'}
                        </span>
                      </span>
                      <span className="truncate text-slate-400">امتیاز کاربران</span>
                    </div>

                    <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-slate-500 sm:h-5 sm:text-[11px]">
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <UserRound className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">{course.instructor}</span>
                      </span>
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                        <span className="truncate">{course.duration}</span>
                      </span>
                    </div>

                    <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-[#64748b] sm:h-5 sm:text-[11px]">
                      <span className="truncate">{course.lessonsCount.toLocaleString('fa-IR')} جلسه</span>
                      <span className="inline-flex min-w-0 items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#C11277]" />
                        <span className="truncate">آنلاین</span>
                      </span>
                    </div>

                    <div className="mt-auto flex h-auto items-end justify-between gap-1">
                      <div className="flex min-h-[2.2rem] flex-col items-start justify-end gap-1 text-right sm:min-h-10">
                        {course.oldPrice > 0 ? (
                          <p className="text-[11px] font-medium text-slate-400 line-through decoration-1 decoration-red-400/80">
                            {course.oldPrice.toLocaleString('fa-IR')} تومان
                          </p>
                        ) : (
                          <p className="text-[11px] text-transparent">-</p>
                        )}
                        <FreeBadge size="sm" className="shadow-none" />
                      </div>
                      <div className="flex min-w-0 items-center gap-1 text-[11px] font-bold text-[#1C3B9B] sm:gap-1.5 sm:text-xs">
                        <span className="truncate sm:hidden">مشاهده</span>
                        <span className="hidden truncate sm:inline">مشاهده جزئیات</span>
                        <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        </section>

        {/* Podcasts Strip with Inline Player */}
        <section dir="rtl" className="border-t border-slate-200/70 py-4 sm:py-8">
          <div className="mb-3 flex items-end justify-between gap-2">
            <div className="text-right">
              <p className="hidden text-sm font-semibold tracking-[0.28em] text-[#1C3B9B] sm:block">شنیدنی های حرفه ای</p>
              {renderSectionTitle('پادکست ها')}
            </div>
            <div className="mb-3 hidden h-px flex-1 bg-gradient-to-l from-slate-300/20 via-slate-300/55 to-transparent sm:block" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handlePodcastCarouselScroll('prev')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید قبلی"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handlePodcastCarouselScroll('next')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="اسلاید بعدی"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/podcasts')}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#475569] transition hover:text-[#1C3B9B] sm:gap-2 sm:text-sm"
              >
                <span>همه پادکست‌ها</span>
                <ArrowLeft className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4 flex flex-nowrap items-center justify-start gap-2 overflow-x-auto p-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-[#eef2ff]/70 sm:p-2">
            {podcastCategoryTabs.map((tab) => (
              <button
                key={`podcast-${tab.key}`}
                type="button"
                onClick={() => setPodcastActiveTab(tab.key)}
                className={`flex-none whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition sm:rounded-xl sm:px-3.5 sm:py-2 sm:text-sm ${
                  podcastActiveTab === tab.key
                    ? 'border-black bg-black text-white sm:border-transparent sm:bg-white sm:text-[#1C3B9B] sm:shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-[#1C3B9B] sm:border-transparent sm:bg-transparent sm:hover:bg-white/70'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setIsPodcastCarouselHovered(true)}
            onMouseLeave={() => setIsPodcastCarouselHovered(false)}
          >
            <div
              ref={podcastCarouselRef}
              className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 pb-3 scroll-smooth hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-3 sm:px-1"
              dir="rtl"
            >
              {loopedPodcastCards.map((podcast, index) => {
                const isCurrent = currentPodcast?.id === podcast.id;
                const progressPercent = isCurrent && duration > 0 ? (currentTime / duration) * 100 : 0;
                const totalDuration = isCurrent ? duration : podcast.duration || 0;
                const hasAudio = !!(podcast.streamUrl || podcast.audioFile);

                return (
                  <motion.article
                    key={podcast.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.45, delay: index * 0.04 }}
                    whileHover={{ y: -5 }}
                    className={`group relative aspect-square w-[11.25rem] shrink-0 snap-start overflow-hidden rounded-[1.25rem] text-right shadow-[0_10px_36px_-14px_rgba(15,23,42,0.35)] transition duration-300 sm:w-[12.5rem] sm:rounded-[1.4rem] ${
                      isCurrent
                        ? 'ring-2 ring-[#1C3B9B] ring-offset-2 ring-offset-white'
                        : 'ring-1 ring-slate-900/10 hover:ring-[#1C3B9B]/35'
                    }`}
                  >
                    <img
                      src={podcast.thumbnail}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      loading={index < 3 ? 'eager' : 'lazy'}
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#020617]/95 via-[#020617]/45 to-[#020617]/10" />

                    <button
                      type="button"
                      onClick={() => navigate(`/podcasts/${podcast.id}`)}
                      className="absolute inset-0 z-[1]"
                      aria-label={`مشاهده ${podcast.title}`}
                    />

                    <div className="absolute start-2.5 top-2.5 z-[2] rounded-full bg-black/45 px-2 py-0.5 text-[9px] font-bold text-white/95 backdrop-blur-md">
                      پادکست
                    </div>

                    {!isCurrent ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePodcastCardPlayPause(podcast);
                        }}
                        disabled={!hasAudio}
                        className={`absolute left-1/2 top-[38%] z-[2] inline-flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 shadow-[0_8px_28px_rgba(0,0,0,0.35)] backdrop-blur-md transition group-hover:scale-105 ${
                          hasAudio
                            ? 'bg-white/95 text-[#1C3B9B] hover:bg-white'
                            : 'cursor-not-allowed bg-white/50 text-slate-400'
                        }`}
                        aria-label="پخش پادکست"
                      >
                        <Play className="h-5 w-5 ms-0.5" />
                      </button>
                    ) : null}

                    <div className="absolute inset-x-0 bottom-0 z-[2] flex flex-col gap-2 p-3 pt-8">
                      <button
                        type="button"
                        onClick={() => navigate(`/podcasts/${podcast.id}`)}
                        className="min-w-0 text-right"
                      >
                        <h3 className="line-clamp-2 text-[13px] font-extrabold leading-5 text-white sm:text-sm">
                          {podcast.title}
                        </h3>
                      </button>

                      <div className="flex min-w-0 items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2 text-[10px] font-semibold text-white/85">
                          <span className="inline-flex shrink-0 items-center gap-0.5 text-amber-300">
                            <Star className="h-3 w-3 fill-current" aria-hidden />
                            <span className="tabular-nums">
                              {(4.6 + ((index % 4) * 0.1)).toLocaleString('fa-IR')}
                            </span>
                          </span>
                          <span className="h-3 w-px shrink-0 bg-white/25" aria-hidden />
                          <span className="inline-flex shrink-0 items-center gap-0.5 tabular-nums">
                            <Clock3 className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
                            {formatTime(totalDuration)}
                          </span>
                        </div>

                        {isCurrent ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePodcastCardPlayPause(podcast);
                            }}
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#1C3B9B] shadow-md transition hover:bg-white/90"
                            aria-label={isPlaying ? 'توقف پادکست' : 'پخش پادکست'}
                          >
                            {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ms-0.5" />}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate(`/podcasts/${podcast.id}`)}
                            className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-bold text-white/90 transition hover:text-white"
                          >
                            <span>جزئیات</span>
                            <ArrowLeft className="h-3 w-3" />
                          </button>
                        )}
                      </div>

                      {isCurrent ? (
                        <div className="min-w-0 space-y-1" dir="ltr">
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={1}
                            value={progressPercent}
                            onChange={(e) => {
                              e.stopPropagation();
                              const percent = parseFloat(e.target.value);
                              seek((percent / 100) * (duration || 0));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-1 w-full cursor-pointer accent-white"
                            aria-label="پیشرفت پخش"
                          />
                          <div className="flex items-center justify-between text-[9px] tabular-nums text-white/65">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(totalDuration)}</span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials Strip */}
        <section dir="rtl" className="border-t border-slate-200/70 py-4 sm:py-8">
          <div className="mb-6 flex items-end gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold tracking-[0.28em] text-[#1C3B9B]">تجربه کاربران</p>
              {renderSectionTitle('نظرات کاربران')}
            </div>
            <div className="mb-3 hidden h-px flex-1 bg-gradient-to-l from-slate-300/20 via-slate-300/55 to-transparent sm:block" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleTestimonialScroll('prev')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="نظر قبلی"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleTestimonialScroll('next')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="نظر بعدی"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div
            className="relative -mx-0.5 pb-1 pt-0.5 sm:hidden"
            dir="ltr"
          >
            <div
              ref={testimonialStripMobileRef}
              className="flex touch-pan-x snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-1 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              onMouseEnter={() => {
                testimonialStripMobilePausedRef.current = true;
              }}
              onMouseLeave={() => {
                testimonialStripMobilePausedRef.current = false;
              }}
              onTouchStart={() => {
                testimonialStripMobilePausedRef.current = true;
              }}
              onTouchEnd={() => {
                window.setTimeout(() => {
                  testimonialStripMobilePausedRef.current = false;
                }, 1800);
              }}
            >
              {[...testimonialCards, ...testimonialCards].map((item, index) => (
                <article
                  key={`${item.id}-strip-${index}`}
                  data-testimonial-strip-card
                  dir="rtl"
                  className="flex w-[min(218px,calc(100vw-4rem))] max-w-[218px] shrink-0 snap-start snap-always flex-col rounded-xl border border-slate-200/90 bg-white px-3 pb-2.5 pt-3 shadow-sm"
                >
                  <div className="flex items-start gap-2.5">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="h-10 w-10 shrink-0 rounded-lg object-cover"
                      loading="lazy"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (img.src.endsWith(encodeURI(testimonialAvatarFallback))) return;
                        img.src = testimonialAvatarFallback;
                      }}
                    />
                    <div className="min-w-0 flex-1 text-right">
                      <p className="break-words text-xs font-semibold leading-snug text-slate-900 [overflow-wrap:anywhere]">
                        {item.name}
                      </p>
                      <p className="mt-0.5 line-clamp-1 text-[11px] text-slate-400">{item.role}</p>
                    </div>
                  </div>
                  <p className="mt-2.5 line-clamp-2 min-h-[2.5rem] text-right text-[11px] leading-relaxed text-slate-500">
                    {item.text}
                  </p>
                  <div className="mt-2.5 flex items-center justify-end gap-0.5 border-t border-slate-100 pt-2 text-[11px] font-medium text-amber-600">
                    <Star className="h-3.5 w-3.5 shrink-0 fill-current opacity-80" aria-hidden />
                    <span>{item.rating.toLocaleString('fa-IR')}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div
            className="relative hidden sm:block"
            onMouseEnter={() => setIsTestimonialHovered(true)}
            onMouseLeave={() => setIsTestimonialHovered(false)}
          >
            <div
              ref={testimonialCarouselRef}
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-4 scroll-smooth hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-5"
              dir="rtl"
            >
              {loopedTestimonialCards.map((item, index) => (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 26, scale: 0.96 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.55, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, rotate: -0.2 }}
                  className="group relative min-w-[78vw] max-w-[78vw] snap-start overflow-hidden rounded-[20px] border border-slate-200/90 bg-white p-4 text-right shadow-[0_16px_30px_-22px_rgba(15,23,42,0.28)] sm:min-w-[300px] sm:max-w-[300px]"
                >
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#1C3B9B]/[0.06] via-transparent to-[#C11277]/[0.06] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <div className="relative z-10">
                    <div className="mb-3 flex items-start justify-between">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-l from-[#1C3B9B]/15 to-[#C11277]/15 text-[#1C3B9B] transition-transform duration-500 group-hover:rotate-6">
                        <Quote className="h-4 w-4" />
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-600 shadow-[0_10px_18px_-14px_rgba(245,158,11,0.7)]">
                        <Star className="h-3.5 w-3.5 fill-current" />
                        {item.rating.toLocaleString('fa-IR')}
                      </span>
                    </div>

                    <p className="line-clamp-4 min-h-[88px] text-sm leading-7 text-slate-700">
                      {item.text}
                    </p>

                    <div className="mt-4 flex items-start gap-3 border-t border-slate-100/90 pt-3">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-[#1C3B9B]/20 transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.src.endsWith(encodeURI(testimonialAvatarFallback))) return;
                          img.src = testimonialAvatarFallback;
                        }}
                      />
                      <div className="min-w-0 flex-1 text-right">
                        <p className="break-words text-sm font-black leading-snug text-[#0f172a]">{item.name}</p>
                        <p className="mt-1 break-words text-xs leading-relaxed text-slate-500">{item.role}</p>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Mentor Biography + Partners Wheel */}
        <section dir="rtl" className="border-t border-slate-200/70 py-4 sm:py-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6 }}
              className="text-right"
            >
              <div className="flex flex-col items-center gap-3">
                <div className="relative h-44 w-44 shrink-0 rounded-full bg-white p-1.5 shadow-[0_18px_30px_-20px_rgba(15,23,42,0.4)] sm:h-52 sm:w-52">
                  <div className="absolute inset-0 rounded-full border-[3px] border-[#1C3B9B]/35" />
                  <div className="absolute inset-1 rounded-full border-2 border-[#C11277]/40" />
                  <img
                    src="/assets/mentor/ostad.png"
                    alt="استاد فراز قورچیان"
                    className="h-full w-full rounded-full object-cover"
                    loading="lazy"
                  />
                </div>
                <p className="text-sm font-semibold tracking-[0.28em] text-[#1C3B9B]">معرفی استاد</p>
              </div>
              <h2 className="mt-3 text-center text-3xl font-black text-[#0f172a] sm:text-4xl">بیوگرافی استاد فراز قورچیان</h2>
              <div className="mt-5 space-y-3 text-sm leading-8 text-slate-700 sm:text-base">
                <p>
                  استاد فراز قورچیان سخنران، استاد معنوی و یکی از بزرگترین اساتید ایران در حوزه معناگرایی و
                  خودشناسی می باشند.
                </p>
                <p>
                  ایشان با برگزاری بیش از ۲۱۹۰ کارگاه حضوری و آنلاین در مورد خودشناسی و پاکسازی ناخودآگاه، تهیه بیش
                  از ۲۰۰ محتوای آموزشی صوتی و تصویری و ارائه آن به صورت حضوری و آنلاین به مردم سرتاسر دنیا،
                  تاثیرگذاری گسترده‌ای ایجاد کرده‌اند.
                </p>
                <p>
                  ارائه سالانه چندین میلیارد خدمات رایگان نظیر کارگاه‌های چند هزار نفره رایگان، سمینارهای رایگان و
                  وبینارهای رایگان، از مهم‌ترین اقدامات ایشان در مسیر توسعه سلامت روان جامعه بوده است.
                </p>
                <p>
                  این مسیر حرفه‌ای باعث شده گام‌های بلند و مؤثری در بهبود کیفیت سلامت روان مردم ایران و جهان برداشته
                  شود.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6 }}
              className="mx-auto w-full max-w-[430px] px-1 sm:px-0"
            >
              <div className="relative mx-auto h-[84vw] w-[84vw] max-h-[340px] max-w-[340px] rounded-full border border-slate-200/80 bg-[#f8fafc] shadow-[0_28px_52px_-30px_rgba(15,23,42,0.28)] sm:h-[440px] sm:w-[440px] sm:max-h-none sm:max-w-none">
                <motion.div
                  aria-hidden
                  animate={{ scale: [1, 1.03, 1], opacity: [0.55, 0.95, 0.55] }}
                  transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
                  className="pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle,_rgba(28,59,155,0.18)_0%,_rgba(193,18,119,0.12)_36%,_rgba(28,59,155,0.02)_66%,_transparent_78%)] blur-[2px]"
                />
                <motion.div
                  aria-hidden
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                  className="pointer-events-none absolute inset-2 rounded-full bg-[conic-gradient(from_45deg,_rgba(28,59,155,0.0),_rgba(28,59,155,0.28),_rgba(193,18,119,0.3),_rgba(28,59,155,0.0))] blur-[1px]"
                />
                <motion.div
                  aria-hidden
                  animate={{ rotate: -360 }}
                  transition={{ duration: 13, repeat: Infinity, ease: 'linear' }}
                  className="pointer-events-none absolute inset-6 rounded-full bg-[conic-gradient(from_180deg,_rgba(193,18,119,0.0),_rgba(193,18,119,0.24),_rgba(28,59,155,0.22),_rgba(193,18,119,0.0))] blur-[1px]"
                />
                <div className="absolute inset-3 rounded-full bg-[#f8fafc]" />
                <div className="absolute inset-8 rounded-full border-2 border-dashed border-[#1C3B9B]/30" />
                <div className="absolute inset-16 rounded-full border border-[#C11277]/28" />
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0"
                >
                  {mentorPartnerLogos.map((logo, index) => {
                    const angle = (360 / mentorPartnerLogos.length) * index;
                    return (
                      <div
                        key={logo.id}
                        className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 sm:h-24 sm:w-24"
                        style={{
                          transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(clamp(-136px,-33vw,-112px)) rotate(-${angle}deg)`,
                        }}
                      >
                        <motion.div
                          animate={{ rotate: -360 }}
                          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                          whileHover={{ scale: 1.12, y: -4 }}
                          className="relative flex h-full w-full items-center justify-center rounded-full border-[3px] border-[#1C3B9B]/35 bg-white p-2 shadow-[0_16px_26px_-14px_rgba(15,23,42,0.35)]"
                        >
                          <motion.div
                            aria-hidden
                            animate={{
                              opacity: [0.12, 0.95, 0.12],
                              scale: [1, 1.12, 1],
                            }}
                            transition={{
                              duration: 30,
                              repeat: Infinity,
                              ease: 'linear',
                              delay: ((mentorPartnerLogos.length - index) % mentorPartnerLogos.length) * (30 / mentorPartnerLogos.length),
                            }}
                            className="pointer-events-none absolute inset-[-8px] rounded-full bg-[radial-gradient(circle,_rgba(193,18,119,0.12)_0%,_rgba(28,59,155,0.14)_45%,_transparent_72%)]"
                          />
                          <div className="pointer-events-none absolute inset-0 rounded-full border-[3px] border-[#C11277]/35 opacity-65" />
                          <img
                            src={logo.src}
                            alt={logo.alt}
                            className="h-full w-full object-contain p-1 sm:p-1.5"
                            loading="lazy"
                          />
                        </motion.div>
                      </div>
                    );
                  })}
                </motion.div>

                <div className="absolute left-1/2 top-1/2 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[#1C3B9B]/25 bg-white text-center shadow-[0_20px_34px_-22px_rgba(28,59,155,0.5)] sm:h-36 sm:w-36">
                  <div>
                    <p className="text-xs font-semibold text-slate-500">همکاری با</p>
                    <p className="mt-1 text-xl font-black text-[#1C3B9B]">۱۱</p>
                    <p className="text-xs font-semibold text-slate-600">سازمان</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Celebrity Collaborations Ticker */}
        <section dir="rtl" className="border-t border-slate-200/70 py-4 sm:py-8">
          <div className="mb-6 text-right">
            <p className="text-sm font-semibold tracking-[0.28em] text-[#1C3B9B]">همکاری‌های ویژه</p>
            <h2 className="mt-2 text-3xl font-black text-[#0f172a] sm:text-4xl">سلبریتی‌ها</h2>
          </div>

          <div className="relative min-w-0 max-w-full overflow-x-hidden bg-transparent px-3 py-7 sm:px-2 sm:py-10">
            <div className="min-w-0 max-w-full lg:hidden">
              <div className="min-w-0 overflow-hidden rounded-2xl py-1">
                <div
                  ref={mobileCelebrityStripRef}
                  className="w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain scroll-smooth py-2 [-ms-overflow-style:none] [scroll-padding-inline:1rem] [scrollbar-width:none] [touch-action:pan-x] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden"
                  onTouchStart={pauseMobileCelebrityAutoplay}
                  onTouchEnd={() => {
                    pauseMobileCelebrityAutoplay();
                    handleMobileCelebrityScrollEnd();
                  }}
                  onScroll={handleMobileCelebrityScroll}
                  onScrollEnd={handleMobileCelebrityScrollEnd}
                >
                  <div dir="rtl" className="flex w-max min-w-full snap-x snap-mandatory items-center gap-4 ps-4 pe-4">
                    {mobileCelebrityLoopSlides.map((item, slideIndex) => (
                      <div
                        key={`${item.src}-loop-${slideIndex}`}
                        data-celebrity-slide
                        className="group relative inline-flex w-[6.75rem] max-w-[calc(100vw-2.5rem)] shrink-0 snap-start flex-col items-center gap-2"
                      >
                        <div className="relative rounded-full bg-gradient-to-l from-[#1C3B9B] via-[#7c3aed] to-[#C11277] p-1.5">
                          <div className="relative flex h-24 w-24 items-center justify-center rounded-full border-[3px] border-white bg-white shadow-[0_20px_34px_-14px_rgba(15,23,42,0.4)]">
                            <motion.div
                              aria-hidden
                              animate={{ rotate: 360 }}
                              transition={{ duration: 2.8, repeat: Infinity, ease: 'linear' }}
                              className="pointer-events-none absolute inset-[-6px] rounded-full bg-[conic-gradient(from_120deg,_rgba(255,255,255,0)_0%,_rgba(255,255,255,0.95)_16%,_rgba(255,255,255,0)_30%,_rgba(255,255,255,0)_100%)]"
                            />
                            <motion.div
                              aria-hidden
                              animate={{ rotate: 360 }}
                              transition={{ duration: 4.5, repeat: Infinity, ease: 'linear' }}
                              className="pointer-events-none absolute inset-0 rounded-full bg-[conic-gradient(from_90deg,_rgba(28,59,155,0),_rgba(28,59,155,0.24),_rgba(193,18,119,0.24),_rgba(28,59,155,0))]"
                            />
                            <div className="pointer-events-none absolute inset-[2px] rounded-full bg-white" />
                            <img
                              src={item.src}
                              alt={item.name}
                              className="relative z-10 h-full w-full rounded-full object-cover p-2"
                              loading="lazy"
                            />
                          </div>
                        </div>
                        <span className="inline-flex max-w-full items-center rounded-full border border-[#1C3B9B]/20 bg-white/95 px-2.5 py-1 text-[10px] font-black text-[#1C3B9B] shadow-[0_10px_16px_-12px_rgba(15,23,42,0.6)]">
                          {item.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div
              ref={desktopCelebrityStripRef}
              className={`hidden overflow-x-auto px-1 sm:px-2 hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] lg:block [&::-webkit-scrollbar]:hidden ${isCelebrityDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              dir="ltr"
              onMouseDown={(e) => {
                if (!desktopCelebrityStripRef.current) return;
                setIsCelebrityDragging(true);
                celebrityDragStartXRef.current = e.clientX;
                celebrityDragStartScrollRef.current = desktopCelebrityStripRef.current.scrollLeft;
              }}
              onMouseMove={(e) => {
                if (!isCelebrityDragging || !desktopCelebrityStripRef.current) return;
                const delta = e.clientX - celebrityDragStartXRef.current;
                desktopCelebrityStripRef.current.scrollLeft = celebrityDragStartScrollRef.current - delta;
              }}
              onMouseUp={() => setIsCelebrityDragging(false)}
              onMouseLeave={() => setIsCelebrityDragging(false)}
            >
              <div className="flex w-max items-center gap-4 sm:gap-7">
                {celebrityTickerItems.map((item, index) => (
                  <motion.div
                    key={`${item.src}-${index}`}
                    whileHover={{ y: -7, scale: 1.1 }}
                    className="group relative flex flex-col items-center gap-2"
                  >
                    <div className="relative rounded-full bg-gradient-to-l from-[#1C3B9B] via-[#7c3aed] to-[#C11277] p-1.5">
                      <div className="relative flex h-24 w-24 items-center justify-center rounded-full border-[3px] border-white bg-white shadow-[0_20px_34px_-14px_rgba(15,23,42,0.4)] sm:h-32 sm:w-32">
                        <motion.div
                          aria-hidden
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2.8, repeat: Infinity, ease: 'linear' }}
                          className="pointer-events-none absolute inset-[-6px] rounded-full bg-[conic-gradient(from_120deg,_rgba(255,255,255,0)_0%,_rgba(255,255,255,0.95)_16%,_rgba(255,255,255,0)_30%,_rgba(255,255,255,0)_100%)]"
                        />
                        <motion.div
                          aria-hidden
                          animate={{ rotate: 360 }}
                          transition={{ duration: 4.5, repeat: Infinity, ease: 'linear' }}
                          className="pointer-events-none absolute inset-0 rounded-full bg-[conic-gradient(from_90deg,_rgba(28,59,155,0),_rgba(28,59,155,0.24),_rgba(193,18,119,0.24),_rgba(28,59,155,0))]"
                        />
                        <div className="pointer-events-none absolute inset-[2px] rounded-full bg-white" />
                        <img
                          src={item.src}
                          alt={item.name}
                          className="relative z-10 h-full w-full rounded-full object-cover p-2 transition duration-500 group-hover:brightness-110"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <span className="inline-flex items-center rounded-full border border-[#1C3B9B]/20 bg-white/95 px-2.5 py-1 text-[10px] font-black text-[#1C3B9B] shadow-[0_10px_16px_-12px_rgba(15,23,42,0.6)]">
                      {item.name}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Latest Articles Carousel */}
        <section dir="rtl" className="border-t border-slate-200/70 py-4 sm:py-8">
          <div className="mb-3 flex items-end justify-between gap-2">
            <div className="text-right">
              <p className="hidden text-sm font-semibold tracking-[0.28em] text-[#1C3B9B] sm:block">محتوای تازه</p>
              {renderSectionTitle('آخرین مقالات')}
            </div>
            <div className="mb-3 hidden h-px flex-1 bg-gradient-to-l from-slate-300/20 via-slate-300/55 to-transparent sm:block" />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleLatestArticlesScroll('prev')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="مقاله قبلی"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => handleLatestArticlesScroll('next')}
                className="hidden h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-[#1C3B9B] shadow-sm transition hover:border-[#1C3B9B] hover:bg-[#1C3B9B]/5 sm:inline-flex"
                aria-label="مقاله بعدی"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => navigate('/articles')}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-[#475569] transition hover:text-[#1C3B9B] sm:gap-2 sm:text-sm"
              >
                <span>همه مقالات</span>
                <ArrowLeft className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mb-4 flex flex-nowrap items-center justify-start gap-2 overflow-x-auto p-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:mb-6 sm:rounded-2xl sm:border sm:border-slate-200 sm:bg-[#eef2ff]/70 sm:p-2">
            {latestArticleTabs.map((tab) => (
              <button
                key={`latest-article-${tab.key}`}
                type="button"
                onClick={() => setLatestArticlesActiveTab(tab.key)}
                className={`flex-none whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition sm:rounded-xl sm:px-3.5 sm:py-2 sm:text-sm ${
                  latestArticlesActiveTab === tab.key
                    ? 'border-black bg-black text-white sm:border-transparent sm:bg-white sm:text-[#1C3B9B] sm:shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-[#1C3B9B] sm:border-transparent sm:bg-transparent sm:hover:bg-white/70'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loopedLatestArticlesCards.length > 0 ? (
            <div
              className="relative"
              onMouseEnter={() => setIsLatestArticlesHovered(true)}
              onMouseLeave={() => setIsLatestArticlesHovered(false)}
            >
              <div
                ref={latestArticlesCarouselRef}
                className="flex snap-x snap-mandatory gap-2 overflow-x-auto px-0.5 pb-3 scroll-smooth hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-5 sm:px-1"
                dir="rtl"
              >
                {loopedLatestArticlesCards.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.45, delay: index * 0.03 }}
                    whileHover={{ y: -6, scale: 1.005 }}
                    className="group relative min-w-[52vw] max-w-[52vw] snap-start overflow-hidden rounded-[14px] border border-slate-200 bg-white text-right shadow-[0_18px_30px_-22px_rgba(15,23,42,0.32)] sm:min-w-[310px] sm:max-w-[310px] sm:rounded-[20px]"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/articles/${article.slug}`)}
                      className="block w-full text-right"
                    >
                      <div className="relative h-28 overflow-hidden sm:h-40">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading={index < 3 ? 'eager' : 'lazy'}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                      </div>

                      <div className="grid min-h-[176px] grid-rows-[2.6rem_1.2rem_1.2rem_1fr] gap-1.5 p-2.5 sm:min-h-[186px] sm:grid-rows-[3rem_1.25rem_1.25rem_1fr] sm:gap-2 sm:p-4">
                        <h3 className="line-clamp-2 h-[2.6rem] text-[15px] font-extrabold leading-5 text-[#0f172a] group-hover:text-[#1C3B9B] sm:h-12 sm:text-base sm:leading-6">
                          {article.title}
                        </h3>

                        <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] sm:h-5 sm:text-[11px]">
                          <span className="inline-flex min-w-0 items-center gap-1 text-amber-500">
                            <Star className="h-3.5 w-3.5 fill-current" />
                            <span className="truncate font-bold">{(4.5 + ((index % 4) * 0.1)).toLocaleString('fa-IR')}</span>
                          </span>
                          <span className="truncate text-slate-400">امتیاز محتوا</span>
                        </div>

                        <div className="flex h-[1.2rem] items-center justify-between gap-1 text-[10px] text-slate-500 sm:h-5 sm:text-[11px]">
                          <span className="inline-flex min-w-0 items-center gap-1">
                            <UserRound className="h-3.5 w-3.5 text-slate-400" />
                            <span className="truncate">تحریریه من حقیقی</span>
                          </span>
                          <span className="inline-flex min-w-0 items-center gap-1">
                            <Clock3 className="h-3.5 w-3.5 text-slate-400" />
                            <span className="truncate">{article.readingTime ? `${article.readingTime.toLocaleString('fa-IR')} دقیقه` : '۶ دقیقه'}</span>
                          </span>
                        </div>

                        <div className="mt-auto flex items-center justify-between gap-1 text-[10px] text-[#64748b] sm:text-[11px]">
                          <span className="truncate">
                            {article.publishedAt
                              ? new Date(String(article.publishedAt)).toLocaleDateString('fa-IR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })
                              : 'به‌تازگی'}
                          </span>
                          <span className="inline-flex min-w-0 items-center gap-1 text-[11px] font-bold text-[#1C3B9B] sm:gap-1.5 sm:text-xs">
                            <span className="truncate sm:hidden">مشاهده</span>
                            <span className="hidden truncate sm:inline">مشاهده مقاله</span>
                            <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                          </span>
                        </div>
                      </div>
                    </button>
                  </motion.article>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-[#64748b]">
              مقاله‌ای برای نمایش وجود ندارد.
            </div>
          )}
        </section>

        {false && (
        <>
        {/* About · Mission - Enhanced with Background */}
        <section className="relative border-t border-white/10 py-16 sm:py-20 overflow-hidden">
          {/* Background Images */}
          <div className="absolute inset-0">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.15 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5 }}
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${curatedAssets.missionImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(60px)',
              }}
            />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
            <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="space-y-6"
              >
                <motion.p
                  variants={fadeUp}
                  className="text-sm font-semibold uppercase tracking-[0.5em] text-violet-400"
                >
                  درباره · ماموریت
                </motion.p>
                <motion.h2 variants={fadeUp} className="text-4xl font-bold sm:text-5xl lg:text-6xl text-right">
                  من حقیقی برای ساختن آینده‌ای است که منتظرش بودی
                </motion.h2>
                <motion.p variants={fadeUp} className="text-lg leading-relaxed text-white/80 text-right">
                  ما تغییر را جرقه می‌زنیم و تحول را تثبیت می‌کنیم. با دوره‌ها و کارگاه‌های من حقیقی، مسیر خودشناسی،
                  کار با ناخودآگاه، روابط عاطفی و روابط مالی را عملی و قابل‌اجرا تجربه می‌کنید.
                </motion.p>
                <motion.div
                  variants={stagger}
                  className="grid gap-6 sm:grid-cols-2"
                >
                  {missionHighlights.map((item) => (
                    <motion.div
                      key={item.label}
                      variants={fadeUp}
                      className="rounded-3xl border border-white/10 bg-white/5 p-6 text-center backdrop-blur"
                    >
                      <p className="text-3xl font-black text-violet-400">{item.value}</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.4em] text-white/60">
                        {item.label}
                      </p>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="relative overflow-hidden rounded-[40px] border border-white/10 bg-white/5"
            >
              <div className="relative h-[500px] sm:h-[600px] lg:h-[700px] w-full">
                <img
                  src="/assets/faraz.jpg"
                  alt="فراز قورچیان"
                  className="h-full w-full object-cover scale-110"
                  style={{ filter: 'blur(20px)' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = curatedAssets.missionImage;
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <img
                    src="/assets/faraz.jpg"
                    alt="فراز قورچیان"
                    className="h-[70%] w-auto max-w-[80%] object-contain rounded-2xl shadow-2xl"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = curatedAssets.missionImage;
                    }}
                  />
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 rounded-3xl border border-white/20 bg-black/50 p-6 backdrop-blur-md">
                <p className="text-xs uppercase tracking-[0.5em] text-violet-400">فراز قورچیان</p>
                <p className="mt-3 text-base leading-relaxed text-white/90 text-right">
                  فراز قورچیان به عنوان محقق، مدرس و سخنران در حوزه خودآگاهی، توسعه فردی و معنا فعالیت می‌کند. 
                  با مدیریت و راهبری تیم من حقیقی، مسیر رشد مخاطبان را با راهبردهای آموزشی و تجربه‌های عملی همراهی می‌کند.
                </p>
              </div>
            </motion.div>
            </div>
          </div>
        </section>

        {/* 3D Globe Section - full width */}
        <section className="relative border-t border-white/10 py-16 sm:py-24 overflow-hidden">
          {/* Soft background glows */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-32 top-0 h-64 w-64 rounded-full bg-violet-400/10 blur-3xl" />
            <div className="absolute -right-32 bottom-0 h-72 w-72 rounded-full bg-purple-500/15 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-8 w-full">
            <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_25px_80px_rgba(0,0,0,0.35)]">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-violet-400/5 via-transparent to-purple-500/10" />

              <div className="relative flex flex-col items-center gap-12 w-full p-6 sm:p-8">
                {/* 3D globe - centered and larger */}
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.4 }}
                  className="w-full flex justify-center"
                >
                  <div className="relative w-full max-w-[800px] aspect-square flex justify-center items-center">
                    {/* Simplified container - no background/border as requested */}
                    <Globe
                      ref={globeRef}
                      backgroundColor="rgba(0,0,0,0)"
                      globeImageUrl="//cdn.jsdelivr.net/npm/three-globe/example/img/earth-blue-marble.jpg"
                      showAtmosphere
                      atmosphereColor="lightskyblue"
                      atmosphereAltitude={0.25}
                      labelsData={places}
                      labelLat={(d: any) => d.properties?.latitude}
                      labelLng={(d: any) => d.properties?.longitude}
                      labelText={(d: any) => d.properties?.name}
                      labelSize={(d: any) => Math.sqrt(d.properties?.pop_max || 1) * 4e-4}
                      labelDotRadius={(d: any) => Math.sqrt(d.properties?.pop_max || 1) * 4e-4}
                      labelColor={() => 'rgba(255,165,0,0.8)'}
                      labelResolution={2}
                      width={window.innerWidth < 768 ? 400 : 700}
                      height={window.innerWidth < 768 ? 400 : 700}
                      onGlobeReady={() => {
                        const controls = globeRef.current?.controls?.();
                        if (controls) {
                          controls.enableZoom = false;
                          controls.enableRotate = true;
                          controls.enablePan = false;
                          controls.autoRotate = true;
                          controls.autoRotateSpeed = 0.8;
                        }
                      }}
                    />
                  </div>
                </motion.div>

                {/* Text - centered below globe */}
                <motion.div
                  variants={stagger}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  className="relative z-10 space-y-6 text-center w-full max-w-4xl flex flex-col items-center"
                >
                  <motion.p
                    variants={fadeUp}
                    className="inline-block rounded-full border border-violet-400/30 bg-violet-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.5em] text-violet-400"
                  >
                    شبکه جهانی من حقیقی
                  </motion.p>
                  <motion.h2
                    variants={fadeUp}
                    className="text-4xl font-bold sm:text-5xl lg:text-6xl leading-tight"
                  >
                    یک کره، هزار داستان تحول
                  </motion.h2>
                  <motion.p
                    variants={fadeUp}
                    className="text-lg leading-relaxed text-white/85"
                  >
                    این کره چرخان نمایی واقعی و نمادین از جامعه جهانی من حقیقی است؛ افرادی از شهرها و
                    کشور‌های مختلف که تصمیم گرفته‌اند نسخه دوم زندگی‌شان را بسازند. هر نقطه، یک انتخاب
                    تازه برای رشد و آگاهی است.
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Podcasts Section - Tony Robbins Style */}
        <section 
          ref={podcastSectionRef}
          className="relative border-t border-white/10 py-24 sm:py-32 overflow-hidden"
        >
          {/* Background Images */}
          <div className="absolute inset-0">
            {curatedAssets.podcastCovers.slice(0, 3).map((img, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, delay: idx * 0.2 }}
                className={`absolute ${idx === 0 ? 'top-0 left-0 w-1/3' : idx === 1 ? 'top-0 right-0 w-1/3' : 'bottom-0 left-1/2 transform -translate-x-1/2 w-1/3'} h-full`}
                style={{
                  backgroundImage: `url(${img})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(80px)',
                }}
              />
            ))}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              {/* Header */}
              <div className="mb-10 flex flex-wrap items-center justify-between gap-2.5 md:flex-row md:gap-5">
                <motion.h3
                  variants={fadeUp}
                  className="text-lg font-medium tracking-tighter text-white text-right"
                >
                  پادکست‌ها
                </motion.h3>
                <motion.a
                  variants={fadeUp}
                  href="/podcasts"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/podcasts');
                  }}
                  className="group flex gap-2 items-center font-medium opacity-60 hover:opacity-100 duration-500 w-auto"
                >
                  <span>همه اپیزودها</span>
                  <svg
                    viewBox="0 0 20 20"
                    className="h-5 w-5 fill-current duration-500 group-hover:translate-x-1 rotate-0"
                  >
                    <title>Carat</title>
                    <path d="M10.9724 10.0006L6.84766 5.87577L8.02616 4.69727L13.3295 10.0006L8.02616 15.3038L6.84766 14.1253L10.9724 10.0006Z" />
                  </svg>
                </motion.a>
              </div>

              {featuredPodcasts.length > 0 ? (
                <ul className="grid grid-cols-1 items-stretch gap-10 rounded-3xl bg-violet-400/10 backdrop-blur-sm border border-violet-400/20 p-5 md:p-8 lg:[grid-template-columns:1.05fr_1.45fr]">
                  {/* Featured Player - First Podcast */}
                  <li className="mx-auto flex w-full max-w-xl flex-col items-center space-y-8 p-4">
                    {featuredPodcasts[0] && (() => {
                      const featuredPodcast = featuredPodcasts[0];
                      const coverImage = curatedAssets.podcastCovers[0];
                      const isCurrentlyPlaying = currentPodcast?.id === featuredPodcast.id;
                      const audioUrl = featuredPodcast.streamUrl || null;
                      const progressPercent = isCurrentlyPlaying && duration > 0 ? (currentTime / duration) * 100 : 0;

                      return (
                        <>
                          <a
                            href={`/podcasts/${featuredPodcast.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/podcasts/${featuredPodcast.id}`);
                            }}
                            className="block"
                          >
                            <div className="relative h-64 w-64 overflow-hidden rounded-xl shadow-xl">
                              <img
                                alt={featuredPodcast.title}
                                loading="lazy"
                                width={256}
                                height={256}
                                className="absolute inset-0 h-full w-full object-cover"
                                src={coverImage}
                              />
                            </div>
                          </a>
                          <div className="text-center w-full">
                            <span className="text-label s mb-2 flex w-full flex-wrap items-center space-x-1 opacity-60 justify-center text-sm">
                              <span>پادکست</span>
                              <span className="mx-2 h-1 w-1 rounded-full bg-white opacity-60"></span>
                              <span>{featuredPodcast.publishedAt ? new Date(String(featuredPodcast.publishedAt)).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' }) : 'اخیر'}</span>
                            </span>
                            <a
                              href={`/podcasts/${featuredPodcast.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/podcasts/${featuredPodcast.id}`);
                              }}
                              className="block"
                            >
                              <h3 className="text-xl text-white hover:text-violet-400 transition-colors text-right">
                                {featuredPodcast.title}
                              </h3>
                            </a>
                          </div>
                          <div className="w-full">
                            <div className="w-full">
                              <input
                                type="range"
                                min="0"
                                max="100"
                                value={isCurrentlyPlaying ? progressPercent : 0}
                                onChange={handleSeek}
                                className="related-podcasts-progress-bar rounded-full bg-white/10 w-full h-1 cursor-pointer accent-violet-400"
                                style={{
                                  background: `linear-gradient(to right, rgb(250, 204, 21) 0%, rgb(250, 204, 21) ${progressPercent}%, rgba(255, 255, 255, 0.1) ${progressPercent}%, rgba(255, 255, 255, 0.1) 100%)`
                                }}
                                disabled={!isCurrentlyPlaying}
                              />
                            </div>
                            <div className="relative flex w-full justify-between pt-2 text-xs text-white/60" dir="ltr">
                              <span>{formatTime(isCurrentlyPlaying ? currentTime : 0)}</span>
                              <span>{formatTime(duration || featuredPodcast.duration || 0)}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-center space-x-6">
                            <button
                              onClick={handlePrevious}
                              disabled={featuredPodcasts.length <= 1}
                              aria-label="go to previous"
                              className={`flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 transition-all duration-200 hover:scale-105 ${featuredPodcasts.length <= 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'}`}
                            >
                              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="24" width="24" className="text-white">
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"></path>
                              </svg>
                            </button>
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={handleJumpBackward}
                                disabled={!isCurrentlyPlaying}
                                aria-label="jump backward"
                                className={`flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 transition-all duration-200 hover:scale-105 ${!isCurrentlyPlaying ? 'opacity-30 cursor-not-allowed' : 'opacity-100'}`}
                              >
                                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" className="h-6 w-6 text-white">
                                  <path fill="none" d="M0 0h24v24H0V0z"></path>
                                  <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8zm-2.44 8.49h.45c.21 0 .37-.05.48-.16s.16-.25.16-.43a.538.538 0 0 0-.15-.39c-.05-.05-.11-.09-.18-.11s-.16-.04-.25-.04c-.08 0-.15.01-.22.03s-.13.05-.18.1-.09.09-.12.15-.05.13-.05.2h-.85a1.06 1.06 0 0 1 .41-.85c.13-.1.27-.18.44-.23s.35-.08.54-.08c.21 0 .41.03.59.08s.33.13.46.23.23.23.3.38.11.33.11.53a.842.842 0 0 1-.17.52 1.1 1.1 0 0 1-.48.39c.24.09.42.21.54.39s.18.38.18.61c0 .2-.04.38-.12.53s-.18.29-.32.39-.29.19-.48.24-.38.08-.6.08c-.18 0-.36-.02-.53-.07s-.33-.12-.46-.23-.25-.23-.33-.38-.12-.34-.12-.55h.85c0 .08.02.15.05.22s.07.12.13.17.12.09.2.11.16.04.25.04c.1 0 .19-.01.27-.04s.15-.07.2-.12.1-.11.13-.18.04-.15.04-.24c0-.11-.02-.21-.05-.29s-.08-.15-.14-.2-.13-.09-.22-.11-.18-.04-.29-.04h-.47v-.65zm5.74.75c0 .32-.03.6-.1.82s-.17.42-.29.57-.28.26-.45.33-.37.1-.59.1-.41-.03-.59-.1-.33-.18-.46-.33-.23-.34-.3-.57-.11-.5-.11-.82v-.74c0-.32.03-.6.1-.82s.17-.42.29-.57.28-.26.45-.33.37-.1.59-.1.41.03.59.1.33.18.46.33.23.34.3.57.11.5.11.82v.74zm-.85-.86c0-.19-.01-.35-.04-.48s-.07-.23-.12-.31-.11-.14-.19-.17-.16-.05-.25-.05-.18.02-.25.05-.14.09-.19.17-.09.18-.12.31-.04.29-.04.48v.97c0 .19.01.35.04.48s.07.24.12.32.11.14.19.17.16.05.25.05.18-.02.25-.05.14-.09.19-.17.09-.19.11-.32c.03-.13.04-.29.04-.48v-.97z"></path>
                              </svg>
                            </button>
                            <button
                              onClick={() => handlePlayPause(featuredPodcast)}
                              disabled={!audioUrl}
                              aria-label="Play"
                              className={`relative h-[72px] w-[72px] rounded-full text-black hover:scale-105 transition-transform ${isCurrentlyPlaying && isPlaying ? 'bg-violet-400' : 'bg-white'} ${!audioUrl ? 'opacity-15 cursor-not-allowed' : ''}`}
                            >
                              {isCurrentlyPlaying && isPlaying ? (
                                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform h-8 w-8">
                                  <path fill="none" d="M0 0h24v24H0z"></path>
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"></path>
                                </svg>
                              ) : (
                                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform h-8 w-8">
                                  <path fill="none" d="M0 0h24v24H0z"></path>
                                  <path d="M8 5v14l11-7z"></path>
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={handleJumpForward}
                              disabled={!isCurrentlyPlaying}
                              aria-label="jump forward"
                              className={`flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 transition-all duration-200 hover:scale-105 ${!isCurrentlyPlaying ? 'opacity-30 cursor-not-allowed' : 'opacity-100'}`}
                            >
                              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" className="h-6 w-6 text-white">
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M18 13c0 3.31-2.69 6-6 6s-6-2.69-6-6 2.69-6 6-6v4l5-5-5-5v4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8h-2z"></path>
                                <path d="M10.06 15.38c-.29 0-.62-.17-.62-.54h-.85c0 .97.9 1.23 1.45 1.23.87 0 1.51-.46 1.51-1.25 0-.66-.45-.9-.71-1 .11-.05.65-.32.65-.92 0-.21-.05-1.22-1.44-1.22-.62 0-1.4.35-1.4 1.16h.85c0-.34.31-.48.57-.48.59 0 .58.5.58.54 0 .52-.41.59-.63.59h-.46v.66h.45c.65 0 .7.42.7.64 0 .32-.21.59-.65.59zM13.85 11.68c-.14 0-1.44-.08-1.44 1.82v.74c0 1.9 1.31 1.82 1.44 1.82.14 0 1.44.09 1.44-1.82v-.74c.01-1.91-1.3-1.82-1.44-1.82zm.6 2.67c0 .77-.21 1.03-.59 1.03s-.6-.26-.6-1.03v-.97c0-.75.22-1.01.59-1.01.38 0 .6.26.6 1.01v.97z"></path>
                              </svg>
                            </button>
                          </div>
                          <button
                            onClick={handleNext}
                            disabled={featuredPodcasts.length <= 1}
                            aria-label="go to next"
                            className={`flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 transition-all duration-200 hover:scale-105 ${featuredPodcasts.length <= 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'}`}
                          >
                            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" height="24" width="24" className="text-white">
                              <path fill="none" d="M0 0h24v24H0z"></path>
                              <path d="m6 18 8.5-6L6 6v12zM16 6v12h2V6h-2z"></path>
                            </svg>
                          </button>
                        </div>
                      </>
                      );
                    })()}
                  </li>

                  {/* Other Podcasts List */}
                  <div className="divide-y divide-violet-400/20">
                    {featuredPodcasts.slice(1).map((podcast, index) => {
                      const coverImage = curatedAssets.podcastCovers[(index + 1) % curatedAssets.podcastCovers.length];
                      const audioUrl = podcast.streamUrl || null;
                      
                      return (
                        <li key={podcast.id} className="flex w-full gap-4 py-4 md:items-center">
                          <a
                            href={`/podcasts/${podcast.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/podcasts/${podcast.id}`);
                            }}
                            className="block"
                          >
                            <div className="relative h-20 w-20 overflow-hidden rounded-lg">
                              <img
                                alt={podcast.title}
                                loading="lazy"
                                width={80}
                                height={80}
                                className="absolute inset-0 h-full object-cover"
                                src={coverImage}
                              />
                            </div>
                          </a>
                          <div className="flex w-full flex-col items-start md:flex-row md:items-center">
                            <div className="mb-3 flex-grow md:mb-0">
                              <span className="text-label s mb-2 flex w-full flex-wrap items-center space-x-1 opacity-60 text-sm">
                                <span>پادکست</span>
                                <span className="mx-2 h-1 w-1 rounded-full bg-white opacity-60"></span>
                                <span>{podcast.publishedAt ? new Date(String(podcast.publishedAt)).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' }) : 'اخیر'}</span>
                              </span>
                              <a
                                href={`/podcasts/${podcast.id}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(`/podcasts/${podcast.id}`);
                                }}
                                className="block"
                              >
                                <h3 className="text-base leading-none tracking-tighter text-white hover:text-violet-400 transition-colors text-right">
                                  {podcast.title}
                                </h3>
                              </a>
                            </div>
                            <button
                              onClick={() => handlePlayPause(podcast)}
                              disabled={!audioUrl}
                              className={`cursor-pointer group inline-flex items-center justify-center gap-2 font-medium text-center tracking-wide rounded-full duration-500 border border-violet-400/30 bg-violet-400/20 hover:bg-violet-400/30 text-white w-auto text-sm py-2 px-4 transition-all ${!audioUrl ? 'opacity-30 cursor-not-allowed' : ''}`}
                            >
                              <svg viewBox="0 0 24 24" fill="transparent" className="flex h-5 w-5 fill-current">
                                <title>Play</title>
                                <path d="M17.2335 11.1362C17.895 11.5221 17.895 12.4779 17.2335 12.8638L6.50387 19.1227C5.83721 19.5116 5 19.0308 5 18.259L5 5.74104C5 4.96925 5.83721 4.48838 6.50387 4.87726L17.2335 11.1362Z" />
                              </svg>
                              <span>
                                <span className="text-nowrap leading-none">گوش دهید</span>
                              </span>
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </div>
                </ul>
              ) : (
                <div className="text-center py-16 border border-white/10 rounded-[32px] bg-[#0a0a0a]/50 backdrop-blur">
                  <p className="text-white/60">پادکستی برای نمایش وجود ندارد</p>
                  <button
                    onClick={() => navigate('/podcasts')}
                    className="mt-4 text-violet-400 hover:text-violet-500 text-sm uppercase tracking-wider"
                  >
                    مشاهده همه پادکست‌ها
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Mentor Section - فراز قورچیان */}
        <section 
          ref={mentorSectionRef}
          className="relative border-t border-white/10 py-24 sm:py-32 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#040404] via-[#0a0a0a] to-[#040404]" />
          
          {/* Parallax Background Images */}
          <motion.div 
            style={{ y: mentorY1, opacity: mentorOpacity }}
            className="absolute inset-0 pointer-events-none"
          >
            <div className="absolute top-0 right-0 w-1/3 h-full">
              <img
                src={curatedAssets.mentorImages[0]}
                alt=""
                className="w-full h-full object-cover opacity-20 blur-sm"
              />
            </div>
            <div className="absolute bottom-0 left-0 w-1/3 h-full">
              <img
                src={curatedAssets.mentorImages[1]}
                alt=""
                className="w-full h-full object-cover opacity-20 blur-sm"
              />
            </div>
          </motion.div>

          <div className="relative mx-auto max-w-6xl px-4 sm:px-8">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="grid gap-12 lg:grid-cols-2 lg:items-center"
            >
              <motion.div variants={fadeUp} className="space-y-6">
                <motion.p
                  variants={fadeUp}
                  className="text-sm font-semibold uppercase tracking-[0.5em] text-violet-400"
                >
                  داستان فراز قورچیان
                </motion.p>
                <motion.h2 
                  variants={fadeUp} 
                  className="text-4xl font-bold sm:text-5xl lg:text-6xl text-right"
                >
                  فراز قورچیان
                  <br />
                  <span className="text-violet-400">مربی تحول و رشد شخصی</span>
                </motion.h2>
                <motion.p 
                  variants={fadeUp} 
                  className="text-lg leading-relaxed text-white/80 text-right"
                >
                  با بیش از یک دهه تجربه در زمینه کوچینگ، روانشناسی تحول و مهندسی رشد شخصی، 
                  فراز قورچیان خالق است. رویکرد او ترکیبی از علم، 
                  تجربه و انرژی است که به هزاران نفر کمک کرده تا نسخه بهتری از خودشان بسازند.
                </motion.p>
                <motion.p 
                  variants={fadeUp} 
                  className="text-base leading-relaxed text-white/70 text-right"
                >
                  هر برنامه، هر کارگاه و هر لحظه از این سفر با دقت طراحی شده تا تو را 
                  به سمت تحولی واقعی و پایدار هدایت کند. این فقط یک برنامه نیست؛ 
                  این یک انقلاب شخصی است.
                </motion.p>
                <motion.div variants={fadeUp} className="flex flex-wrap gap-4 pt-4">
                  <button
                    onClick={() => navigate('/about')}
                    className="rounded-full bg-gradient-to-r from-violet-400 via-violet-500 to-violet-600 px-8 py-3 text-sm font-semibold uppercase tracking-widest text-black shadow-[0_25px_60px_-20px_rgba(139,92,246,0.8)] transition hover:scale-105"
                  >
                    درباره مربی
                  </button>
                  <button
                    onClick={() => navigate('/workshops')}
                    className="rounded-full border border-white/30 px-8 py-3 text-sm font-semibold uppercase tracking-widest text-white transition hover:border-white hover:bg-white/10"
                  >
                    کارگاه‌ها
                  </button>
                </motion.div>
              </motion.div>

              <motion.div 
                variants={fadeUp}
                style={{ y: mentorY2 }}
                className="relative"
              >
                <div className="grid grid-cols-2 gap-4">
                  {curatedAssets.mentorImages.slice(0, 4).map((img, idx) => (
                    <motion.div
                      key={idx}
                      whileHover={{ scale: 1.05, zIndex: 10 }}
                      className={`relative overflow-hidden rounded-[24px] border border-white/20 bg-white/5 backdrop-blur ${
                        idx === 0 ? 'col-span-2 h-64' : 'h-48'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Mentor ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      {idx === 0 && (
                        <div className="absolute bottom-4 left-4 right-4">
                          <p className="text-xs uppercase tracking-[0.5em] text-violet-400 mb-2">
                            چشم‌انداز و ماموریت
                          </p>
                          <p className="text-white/90 text-sm font-medium text-right">
                            ساختن آینده‌ای که منتظرش بودی
                          </p>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="border-t border-white/10 py-12 sm:py-16">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="mb-10 flex flex-col gap-4">
              <motion.p
                variants={fadeUp}
                className="text-sm font-semibold uppercase tracking-[0.5em] text-violet-400"
              >
                دوره‌های آموزشی
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-4xl font-bold sm:text-5xl text-right">
                برنامه‌های تحول
              </motion.h2>
              <motion.p variants={fadeUp} className="max-w-3xl text-base text-white/70 text-right">
                مجموعه دوره‌های عمیق و کاربردی ما که برای جهش شخصی و حرفه‌ای طراحی شده‌اند.
                هر دوره یک سفر کامل برای ساخت نسخه بهتر از خودت.
              </motion.p>
            </div>
            {courses.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-3">
                {courses.slice(0, 6).map((course, index) => {
                  const courseImage = course.thumbnail 
                    ? getImageUrlWithFallback(course.thumbnail, curatedAssets.gallery[index % curatedAssets.gallery.length])
                    : curatedAssets.gallery[index % curatedAssets.gallery.length];
                  
                  return (
                    <motion.div
                      key={course.id}
                      variants={fadeUp}
                      whileHover={{ y: -8, scale: 1.02 }}
                      onClick={() => navigate(`/courses/${getCoursePathSegment(course)}`)}
                      className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-[#0a0a0a] cursor-pointer transition-all duration-300 hover:border-violet-500/30 hover:shadow-[0_25px_60px_-20px_rgba(139,92,246,0.3)]"
                    >
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={courseImage}
                          alt={course.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                        <div className="absolute top-4 right-4">
                          <span className="px-3 py-1 bg-violet-400/20 backdrop-blur rounded-full text-xs font-semibold text-violet-400 uppercase tracking-wider">
                            دوره
                          </span>
                        </div>
                        {course.price > 0 && (
                          <div className="absolute bottom-4 left-4">
                            <span className="px-3 py-1 bg-black/60 backdrop-blur rounded-full text-xs font-semibold text-white">
                              {course.price.toLocaleString()} تومان
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold mb-3 text-white group-hover:text-violet-400 transition-colors line-clamp-2">
                          {course.title}
                        </h3>
                        {course.description && (
                          <p className="text-sm text-white/70 mb-4 line-clamp-3 leading-relaxed">
                            {course.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between pt-4 border-t border-white/10">
                          <div className="flex items-center gap-4 text-xs text-white/60">
                            {course.videos && course.videos.length > 0 && (
                              <span>{course.videos.length} ویدیو</span>
                            )}
                            {course.audios && course.audios.length > 0 && (
                              <span>{course.audios.length} فایل صوتی</span>
                            )}
                          </div>
                          <svg
                            className="w-5 h-5 text-violet-400 transform group-hover:translate-x-1 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 border border-white/10 rounded-[32px] bg-[#0a0a0a]">
                <p className="text-white/60">دوره‌ای برای نمایش وجود ندارد</p>
                <button
                  onClick={() => navigate('/courses')}
                  className="mt-4 text-violet-400 hover:text-violet-500 text-sm uppercase tracking-wider"
                >
                  مشاهده همه دوره‌ها
                </button>
              </div>
            )}
            {courses.length > 6 && (
              <motion.div variants={fadeUp} className="mt-8 text-center">
                <button
                  onClick={() => navigate('/courses')}
                  className="rounded-full border border-white/30 px-8 py-3 text-sm font-semibold uppercase tracking-widest text-white transition hover:border-white hover:bg-white/10"
                >
                  مشاهده همه دوره‌ها
                </button>
              </motion.div>
            )}
          </motion.div>
        </section>

        {/* Articles Section */}
        <section className="border-t border-white/10 py-12 sm:py-16">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="mb-10 flex flex-col gap-4">
              <motion.p
                variants={fadeUp}
                className="text-sm font-semibold uppercase tracking-[0.5em] text-violet-400"
              >
                مقالات
              </motion.p>
              <motion.h2 variants={fadeUp} className="text-4xl font-bold sm:text-5xl text-right">
                مقالات برتر
              </motion.h2>
              <motion.p variants={fadeUp} className="max-w-3xl text-base text-white/70 text-right">
                مجموعه مقالات منتخب ما که برای تحول ذهنی و پیشرفت شخصی طراحی شده‌اند.
                هر مقاله یک راهنمای عملی برای ساخت زندگی بهتر.
              </motion.p>
            </div>
            {articlesLoading && articlesList.length === 0 ? (
              <div className="flex justify-center py-16">
                <SiteBrandedLoader
                  variant="section"
                  tone="dark"
                  size="sm"
                  message="در حال بارگذاری مقالات..."
                  className="min-h-0 bg-transparent py-0"
                />
              </div>
            ) : articlesList.length > 0 ? (
              <>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                  {articlesList.map((article, index) => {
                    const fallbackImage = curatedAssets.gallery[index % curatedAssets.gallery.length];
                    const articleImage = getImageUrlWithFallback(article.featuredImage, fallbackImage);
                    return (
                      <motion.div
                        key={article.id}
                        variants={fadeUp}
                        whileHover={{ y: -8, scale: 1.02 }}
                        onClick={() => navigate(`/articles/${article.slug}`)}
                        className="group relative overflow-hidden rounded-[32px] border border-white/10 bg-[#0a0a0a] cursor-pointer transition-all duration-300 hover:border-violet-500/30 hover:shadow-[0_25px_60px_-20px_rgba(139,92,246,0.3)]"
                      >
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={articleImage}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={(e) => {
                              const t = e.target as HTMLImageElement;
                              if (t.src !== fallbackImage) t.src = fallbackImage;
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                          <div className="absolute top-3 right-3">
                            <span className="px-2 py-0.5 bg-violet-400/20 backdrop-blur rounded-full text-xs font-semibold text-violet-400 uppercase tracking-wider">
                              مقاله
                            </span>
                          </div>
                        </div>
                        <div className="p-4">
                          <h3 className="text-base font-bold mb-2 text-white group-hover:text-violet-400 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          {article.excerpt && (
                            <p className="text-xs text-white/70 line-clamp-2 leading-relaxed">
                              {article.excerpt}
                            </p>
                          )}
                          <div className="flex items-center justify-between pt-3 border-t border-white/10 mt-2">
                            <span className="text-xs uppercase tracking-[0.2em] text-violet-400">
                              بیشتر
                            </span>
                            <svg className="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                {/* Pagination */}
                {articlesTotalPages > 1 && (
                  <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setArticlesPage((p) => Math.max(1, p - 1))}
                      disabled={articlesPage <= 1 || articlesLoading}
                      className="rounded-lg border border-white/20 px-4 py-2 text-sm text-white disabled:opacity-50 hover:bg-white/10 transition"
                    >
                      قبلی
                    </button>
                    <span className="px-4 py-2 text-sm text-white/80">
                      صفحه {articlesPage} از {articlesTotalPages} ({articlesTotal} مقاله)
                    </span>
                    <button
                      type="button"
                      onClick={() => setArticlesPage((p) => Math.min(articlesTotalPages, p + 1))}
                      disabled={articlesPage >= articlesTotalPages || articlesLoading}
                      className="rounded-lg border border-white/20 px-4 py-2 text-sm text-white disabled:opacity-50 hover:bg-white/10 transition"
                    >
                      بعدی
                    </button>
                  </motion.div>
                )}
                <motion.div variants={fadeUp} className="mt-8 text-center">
                  <button
                    onClick={() => navigate('/articles')}
                    className="rounded-full border border-white/30 px-8 py-3 text-sm font-semibold uppercase tracking-widest text-white transition hover:border-white hover:bg-white/10"
                  >
                    مشاهده همه مقالات
                  </button>
                </motion.div>
              </>
            ) : (
              <div className="text-center py-16 border border-white/10 rounded-[32px] bg-[#0a0a0a]">
                <p className="text-white/60">مقالاتی برای نمایش وجود ندارد</p>
                <button
                  onClick={() => navigate('/articles')}
                  className="mt-4 text-violet-400 hover:text-violet-500 text-sm uppercase tracking-wider"
                >
                  مشاهده همه مقالات
                </button>
              </div>
            )}
          </motion.div>
        </section>

        <section className="border-t border-white/10 py-12 sm:py-16">
          <div className="mb-10 flex flex-col gap-4">
            <p className="text-sm font-semibold uppercase tracking-[0.5em] text-violet-400">
              نظرات
            </p>
            <h2 className="text-4xl font-bold sm:text-5xl text-right">صداهای تحول</h2>
            <p className="max-w-3xl text-base text-white/70 text-right">
              روایت‌هایی که نشان می‌دهد وقتی تصمیم می‌گیری پروژه زندگی‌ات را خودت هدایت کنی و قدرت را انتخاب کنی چه
              اتفاقی می‌افتد.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {testimonialEntries.map((testimonial) => (
              <motion.div
                key={testimonial.quote}
                variants={fadeUp}
                whileHover={{ y: -4 }}
                className="rounded-[32px] border border-white/10 bg-[#0a0a0a] p-8"
              >
                <p className="text-sm leading-relaxed text-white/80">"{testimonial.quote}"</p>
                <div className="mt-6 text-xs uppercase tracking-[0.5em] text-violet-400">
                  {testimonial.name}
                </div>
                <p className="mt-2 text-xs text-white/50">{testimonial.role}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 py-12 sm:py-16">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="relative overflow-hidden rounded-[40px] border border-white/10 bg-gradient-to-r from-black via-[#0b0b0b] to-black p-10"
          >
            <div className="pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-violet-500/20 to-transparent" />
            <div className="relative space-y-6">
              <p className="text-sm font-semibold uppercase tracking-[0.5em] text-violet-400">
                فراخوان نهایی · سفر تو از اینجا شروع می‌شود
              </p>
              <h2 className="text-4xl font-bold sm:text-5xl text-right">
                پتانسیل بی‌پایان خود را کشف کن · پروژه زندگی‌ات را خودت بساز
              </h2>
              <p className="max-w-3xl text-base text-white/70 text-right">
                این Engine برای ساختن آینده‌ای است که مدت‌ها منتظرش بودی. تصمیم بگیر، اقدام کن و اجازه بده نسخه V2
                زندگی‌ات روی صحنه طلایی بیاید.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={handlePrimaryCta}
                  className="rounded-full bg-white px-8 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-black transition hover:bg-white/90"
                >
                  عضویت در من حقیقی
                </button>
                <button
                  onClick={() => navigate('/courses')}
                  className="rounded-full border border-white/30 px-8 py-3 text-sm font-semibold uppercase tracking-[0.4em] text-white transition hover:border-white hover:bg-white/10"
                >
                  کاوش برنامه‌ها
                </button>
              </div>
            </div>
          </motion.div>
        </section>
        </>
        )}
      </main>

      {useFloatingPlayerChrome && currentPodcast ? (
        <div className="fixed inset-x-0 bottom-4 z-[90] px-3 sm:bottom-6 sm:px-6">
          <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-white/20 bg-[#0b1224]/95 text-white shadow-[0_26px_60px_-30px_rgba(15,23,42,0.9)] backdrop-blur-xl">
            {/* Progress bar at top */}
            <div
              className="h-0.5 bg-gradient-to-r from-[#1C3B9B] to-[#C11277] transition-all duration-300"
              style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
            />

            <div className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
              {/* Cover image */}
              {currentPodcast.thumbnail ? (
                <img
                  src={currentPodcast.thumbnail}
                  alt={currentPodcast.title}
                  className="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-white/15 sm:h-14 sm:w-14"
                />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1C3B9B] to-[#C11277] sm:h-14 sm:w-14">
                  <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                  </svg>
                </div>
              )}

              {/* Podcast info */}
              <div className="min-w-0 flex-1 text-right" dir="rtl">
                <p className="truncate text-sm font-bold leading-snug">{currentPodcast.title}</p>
                <p className="mt-0.5 truncate text-[11px] text-slate-300/80">
                  {currentPodcast.description ? currentPodcast.description : 'در حال پخش پادکست'}
                </p>
              </div>

              {/* Controls */}
              <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                <button type="button" onClick={handlePrevious}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-slate-200 transition hover:border-[#a5b4fc] hover:text-white"
                  aria-label="اپیزود قبلی">
                  <SkipBack className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={handleJumpBackward}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-slate-200 transition hover:border-[#a5b4fc] hover:text-white"
                  aria-label="۳۰ ثانیه عقب">
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={togglePlayPause}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#1C3B9B] to-[#C11277] text-white transition hover:scale-[1.05]"
                  aria-label={isPlaying ? 'توقف' : 'پخش'}>
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-0.5" />}
                </button>
                <button type="button" onClick={handleJumpForward}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-slate-200 transition hover:border-[#a5b4fc] hover:text-white"
                  aria-label="۳۰ ثانیه جلو">
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={handleNext}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-slate-200 transition hover:border-[#a5b4fc] hover:text-white"
                  aria-label="اپیزود بعدی">
                  <SkipForward className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Close */}
              <button type="button" onClick={closePlayer}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/20 text-slate-300 transition hover:border-white/45 hover:text-white"
                aria-label="بستن">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Progress + time + speed */}
            <div className="flex items-center gap-2 px-3 pb-3 text-[11px] text-slate-300/85 sm:px-4 sm:pb-4" dir="ltr">
              <span className="w-9 shrink-0 font-mono tabular-nums">{formatTime(currentTime)}</span>
              <input
                type="range"
                min={0}
                max={100}
                step={0.1}
                value={duration > 0 ? (currentTime / duration) * 100 : 0}
                onChange={handleSeek}
                className="h-1 w-full cursor-pointer accent-[#a5b4fc]"
                aria-label="پیشرفت پخش"
              />
              <span className="w-9 shrink-0 text-right font-mono tabular-nums">{formatTime(duration)}</span>
              <div className="flex shrink-0 items-center gap-0.5">
                {PODCAST_PLAYBACK_RATES.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => setPlaybackRate(rate)}
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold transition ${
                      playbackRate === rate
                        ? 'bg-[#a5b4fc]/25 text-[#a5b4fc] ring-1 ring-[#a5b4fc]/50'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                    aria-pressed={playbackRate === rate ? true : false}
                  >
                    {rate}x
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Video Podcast Modal */}
      <VideoPodcastModal
        videoPodcast={selectedVideoPodcast}
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideoModal}
      />
    </div>
  );
};

export default HomeV2;
